

import React, { Suspense, lazy, useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { GameState, GamePhase, Player, Card as CardData, CardType, CardColor, PreparedCard, GameMode, MultiplayerState, ServerMessage, GameAction, HeroId, AnimationData, FlourishAnimationData, AnimationTargets, RoomListEntry, LobbyChatMessage } from './types';
import ActivationChainLayer from './components/ActivationChainLayer';
import ActivationButton from './components/ActivationButton';
import * as gameLogic from './services/gameLogic';
import { applyGameAction } from './services/actions';
import { groupHandForDisplay, isCardVisibleDueToDiscerningEye, isCardVisibleDueToPsyBlock, cleanupHandRotations, getTargetingArrowData, getPhaseMessage as getPhaseMessageUI, calcOpponentHandSpacing, calcIsHandScrollable } from './services/ui';
import { computeNewAnimations } from './services/animations';
import { useBoardInteractions } from './hooks/useBoardInteractions';
import * as agents from './services/agents';
import { chooseLockedCardToReturnFromOverflow, getBestHeroAndClass, getOptimalTowerColor, isValidCounter as isValidCounterLocal } from './services/ai';

import { xmur3, mulberry32 } from './services/random';
import { getPlayPolicy } from './services/policy';
import HandScroller from './components/HandScroller';
import HUD from './components/HUD';
import { useUiActions } from './hooks/useUiActions';
import { useConfirm } from './hooks/useConfirm';
import { useClipboard } from './hooks/useClipboard';
import { useNavigation } from './hooks/useNavigation';
import { useGameEffects } from './hooks/useGameEffects';
import { useHandScroll } from './hooks/useHandScroll';
import { usePrevious } from './hooks/usePrevious';

import Card from './components/Card';
import TouchCardWrapper from './components/TouchCardWrapper';
import { isTouchDevice } from './services/device';
import { useTouchHandScroll } from './hooks/useTouchHandScroll';
import { HealthIcon, StashDeckDisplay, DealingDeckDisplay, TowerIcon, PlayerInfo, ShieldIcon, GameLog, MenuIcon, STASH_DECK_COMPOSITION, DECK_COMPOSITION, getCardDisplayName } from './constants';
import DeckInfoPanel from './components/DeckInfoPanel';
import ProtectorChoiceModal from './components/ProtectorChoiceModal';
import HibernationChoiceModal from './components/HibernationChoiceModal';
import EssenceManager from './components/EssenceManager';
import SeedManager from './components/SeedManager';
import TargetingArrow from './components/TargetingArrow';
import AnimationLayer from './components/AnimationLayer';
import FlourishCard from './components/FlourishCard';
import PlayerAvatar from './components/PlayerAvatar';
import TowerChoiceModal from './components/TowerChoiceModal';
import TowerPurchaseModal from './components/TowerPurchaseModal';
import TowerChangeModal from './components/TowerChangeModal';
import LockedCardOverflowModal from './components/LockedCardOverflowModal';
import ChromabowChoiceModal from './components/ChromabowChoiceModal';
import MageSpellModal from './components/MageSpellModal';
import HeroAbilityHUD from './components/HeroAbilityHUD';
import GameOverFlow from './components/GameOverFlow';
import BattleLogHint from './components/BattleLogHint';
import BattleLog from './components/BattleLog';
import InstructorGuide from './components/InstructorGuide';
import { setUiPack, preloadAllAssets } from './services/assets';
import LoadingScreen from './components/LoadingScreen';
import { AttackIcon, DefenseIcon, HealIcon, DrawIcon, StealIcon, ShockIcon, EnergizeIcon, HibernationIcon, PushbackIcon, DispelIcon, DoubleCrossIcon, BodyUpgradeIcon, FortunesFavorIcon, PsyBlockIcon, PadlockIcon, DiseaseIcon, BrokenSecretIcon, CounterblastIcon, ChromabowIcon, CursedIronIcon, DiscerningEyeIcon, AleIcon, EnergySwordIcon, EyesOfMadnessIcon, FlyingScytheIcon, FrostIcon, InfernoGauntletIcon, InvincibleIcon, LarcendaggerIcon, LifeChargerIcon, LifeDrainIcon, MadnessControlIcon, NaturalGripIcon, PaladinsHammerIcon, PlantedBombIcon, ProtectorIcon, getCardDisplayNameForCard } from './constants';
import MainMenu from './components/MainMenu';
import GameModeMenu from './components/GameModeMenu';
import MultiplayerMenu from './components/MultiplayerMenu';
import OptionsScreen from './components/OptionsScreen';
import InGameMenu from './components/InGameMenu';
import ConfirmDialog from './components/ConfirmDialog';
import CreateRoomScreen from './components/CreateRoomScreen';
import JoinRoomScreen from './components/JoinRoomScreen';
import RoomLobby from './components/RoomLobby';
import HeroClassChoiceModal from './components/HeroClassChoiceModal';
import BrokenSecretRevealModal from './components/BrokenSecretRevealModal';
import AuthScreen from './components/AuthScreen';
import TriffleUnlockOverlay from './components/TriffleUnlockOverlay';
import GlobalMuteButton from './components/GlobalMuteButton';
import GameConnectionManager from './components/GameConnectionManager';
import LanguageSwitcher from './components/LanguageSwitcher';
import NotificationBell from './components/NotificationBell';
import { addNotification } from './services/notificationService';
import { Language, t } from './data/translations';
import CheersAnimation from './components/CheersAnimation';
import TakePutMugAnimation from './components/TakePutMugAnimation';
import StaticMug, { StaticMugHandle } from './components/StaticMug';
import FullscreenButton from './components/FullscreenButton';
import { useMugAnimationController } from './hooks/useMugAnimationController';
import veriskAvatar from './assets/custom/VeriskAvatar.png'
import erokBuruAvatar from './assets/custom/Erok-BuruAvatar.png'
import warriorPng from './assets/custom/Warrior.png'
import magePng from './assets/custom/Mage.png'
import occultistPng from './assets/custom/Occultist.png'
import astridAvatarPng from './assets/custom/AstridAvatar.png'
import { wsClient } from './services/websocketClient';
import { webrtcClient } from './services/webrtcClient';
import { useMultiplayer } from './hooks/useMultiplayer';
import ActionToast from './components/ActionToast';
import TableBackground from './components/TableBackground';
import { getAsset } from './services/assets';
import { useHints } from './hooks/useHints';
import { getAvailablePrepareSlots } from './services/gameLogic';
import { fetchZones, fetchHeros } from './services/okMainApi';
import { getApiBase } from './services/apiBase';
import StashChoiceModal from './components/StashChoiceModal';
import { useBrokenSecret } from './hooks/useBrokenSecret';
import { logAIAction, logAIActionResult, logAIPreparation, logAISpecialAction } from './services/aiLogger';
import { useActionHints } from './hooks/useActionHints';
import { CardWaveSignal, ElementWaveSignal } from './components/WaveSignal';
import { useGameSettings } from './hooks/useGameSettings';
import AudioPlayer, { ensureMusicPlaying } from './components/AudioPlayer';
import { v4 as uuidv4 } from 'uuid'
import { warmAllGameAssets } from './services/assetCache';
import ShellSlot from './components/shell/ShellSlot';
import GameViewport from './components/shell/GameViewport';
import { useRegisterUiAnchor } from './hooks/useRegisterUiAnchor';
import { UI_ANCHOR_IDS, getPreparedCardAnchorId, uiAnchorRegistry } from './services/uiAnchorRegistry';
const logSpace = (..._args: any[]) => { };

const LazyWorldMap = lazy(() => import('./components/WorldMap'));
const LazyProfileScreen = lazy(() => import('./components/ProfileScreen'));
const LazyAdminPanel = lazy(() => import('./components/AdminPanel'));
const LazyEffectPreviewer = lazy(() => import('./components/EffectPreviewer'));
const LazyTestRunner = lazy(() => import('./components/TestRunner'));







interface HandDisplayItem {
  key: string;
  topCard: CardData;
  cards: CardData[];
  count: number;
}

interface DragInfo {
  card: CardData;
  rotation: number;
  source: string;
}


const App: React.FC = () => {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [auth, setAuth] = useState<{
    username: string;
    password?: string;
    token?: string;
    opalstoneId?: string;
    emoteSlots?: { id: string, type: 'premium' | 'animated' }[];
  } | null>(() => {
    try {
      const raw = localStorage.getItem('oko_auth')
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed && parsed.username && parsed.token) return parsed
      }
      return null
    } catch { return null }
  })
  const [showTriffleUnlock, setShowTriffleUnlock] = useState(false);
  const [gameMode, setGameMode] = useState<GameMode>(() => (auth?.token ? 'MAIN_MENU' : 'AUTH'));
  const [lastGameMode, setLastGameMode] = useState<GameMode>('MAIN_MENU');
  const [playerIsReady, setPlayerIsReady] = useState(false);
  const [language, setLanguage] = useState<Language>(() => {
    return (localStorage.getItem('oko_lang') as Language) || 'cn';
  });

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    localStorage.setItem('oko_lang', lang);
  };

  // Asset preloading state
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [availableHeroes, setAvailableHeroes] = useState<HeroId[]>([]);

  useEffect(() => {
    const loadHeroes = async () => {
      try {
        // 1. Fetch all hero definitions (metadata)
        let allHeroes: any[] = [];
        try {
          allHeroes = await fetchHeros();
        } catch (e) {
          console.warn('Failed to fetch heroes from API, using defaults.', e);
        }

        const unlocked: HeroId[] = [];
        const base = getApiBase();

        // 2. Try to fetch user data from server if we have a token
        if (auth?.token) {
          try {
            const resp = await fetch(`${base}/api/me?token=${auth.token}`);
            if (resp.ok) {
              const data = await resp.json();
              if (data.ok && data.user && Array.isArray(data.user.heroes)) {
                data.user.heroes.forEach((hName: string) => {
                  const id = hName.toUpperCase().replace(/[\s-]/g, '_') as HeroId;
                  if (!unlocked.includes(id)) unlocked.push(id);
                });
                setAvailableHeroes(unlocked);
                return; // Success!
              }
            }
          } catch (e) {
            console.warn('[App] Failed to fetch user data for heroes:', e);
          }
        }

        // 3. Fallback: LocalStorage (Offline or server error)
        console.warn('[App] Using localStorage fallback for heroes.');
        const capturedRaw = localStorage.getItem('capturedHeroes');
        const capturedIds: number[] = capturedRaw ? JSON.parse(capturedRaw) : [];

        if (allHeroes && Array.isArray(allHeroes)) {
          allHeroes.forEach(h => {
            if (capturedIds.includes(h.id)) {
              const name = h.name.toUpperCase().replace(/[\s-]/g, '_');
              if (!unlocked.includes(name as HeroId)) {
                unlocked.push(name as HeroId);
              }
            }
          });
        }

        // Fallback for initial heroes based on stored faction (unreliable but better than nothing)
        try {
          const storedFaction = localStorage.getItem('factionId') || (auth as any)?.faction; // Try to get from auth if available
          // Note: auth state might not have faction if it was just loaded from simple token storage
          // But let's check the auth object we have in state
          const faction = (auth as any)?.faction;

          if (faction === 'HARMONIZED' || (storedFaction && parseInt(storedFaction) === 1)) {
            if (!unlocked.includes('NORIM')) unlocked.push('NORIM');
            if (!unlocked.includes('TERRIK')) unlocked.push('TERRIK');
          }
          if (faction === 'CHAOTIC' || (storedFaction && parseInt(storedFaction) === 2)) {
            if (!unlocked.includes('EVELION')) unlocked.push('EVELION');
            if (!unlocked.includes('CORIL')) unlocked.push('CORIL');
          }
          if (faction === 'UNTOUCHED' || (storedFaction && parseInt(storedFaction) === 3)) {
            if (!unlocked.includes('ASTRID')) unlocked.push('ASTRID');
            if (!unlocked.includes('FERRIM')) unlocked.push('FERRIM');
          }
        } catch { }

        setAvailableHeroes(unlocked);
      } catch (e) {
        console.error('Failed to load heroes logic', e);
      }
    };
    loadHeroes();
  }, [auth?.token]); // Re-run when auth token changes

  // Poll for ban status
  useEffect(() => {
    if (!auth?.token) return;
    const checkBan = async () => {
      try {
        const res = await fetch(`${getApiBase()}/api/me?token=${auth.token}`);
        if (res.status === 403) {
          const body = await res.json();
          if (body.error === 'ACCOUNT_BANNED') {
            localStorage.setItem('auth_token', '');
            localStorage.clear();
            window.location.href = '/';
          }
        }
      } catch { }
    };
    checkBan(); // Check immediately
    const interval = setInterval(checkBan, 300000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, [auth?.token]);

  // --- Reconnection Logic moved to GameConnectionManager ---



  const [isDealing, setIsDealing] = useState(false);
  const handContainerRef = useRef<HTMLDivElement>(null);

  const [animations, setAnimations] = useState<AnimationData[]>([]);
  const [flourishAnimations, setFlourishAnimations] = useState<FlourishAnimationData[]>([]);
  const preparedCardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const lastKnownCardRects = useRef<{ [key: string]: DOMRect }>({});
  const effectCardRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [handLayoutTick, setHandLayoutTick] = useState(0);
  const handGroupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const suppressGapRef = useRef(false);
  const playerAvatarRef = useRef<HTMLDivElement>(null);
  const aiAvatarRef = useRef<HTMLDivElement>(null);
  const deckZoneRef = useRef<HTMLDivElement>(null);
  const prepZoneRef = useRef<HTMLDivElement>(null);
  const preparedAnchorIdsRef = useRef<Set<string>>(new Set());
  // Ref for activation chain zones - used for touch hit-testing counter card drops
  const activationZonesRef = useRef<Array<{ activationId: string; rect: DOMRect }>>([]);
  const prevGameState = usePrevious(gameState);
  // Connect effectsEnabled from settings to particlesEnabled
  const { settings: gameSettings } = useGameSettings();
  const particlesEnabled = gameSettings.effectsEnabled;
  const [cheersPlaying, setCheersPlaying] = useState(false);
  const staticMugRef = useRef<StaticMugHandle>(null);
  const lastChainVisualKeyRef = useRef<string | null>(null);

  const [draggedCardInfo, setDraggedCardInfo] = useState<DragInfo | null>(null);
  const [showDefenseGhost, setShowDefenseGhost] = useState(false);
  const [handCardRotations, setHandCardRotations] = useState<Record<string, number>>({});
  const [handGapIndex, setHandGapIndex] = useState<number | null>(null);

  const [targeting, setTargeting] = useState<{ card: PreparedCard; startRect: DOMRect; } | null>(null);


  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const [aiTargetingArrow, setAiTargetingArrow] = useState<{ start: { x: number; y: number }; end: { x: number; y: number }; color: CardColor } | null>(null);
  const [activationResolved, setActivationResolved] = useState(false);
  const [protectorChoiceCardId, setProtectorChoiceCardId] = useState<string | null>(null);
  const openProtectorChoice = (cardId: string) => {
    setProtectorChoiceCardId(cardId)
  }

  const [showInGameMenu, setShowInGameMenu] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'info' | 'error'>('info');
  const [isSwitchingRoom, setIsSwitchingRoom] = useState(false);
  const getAnchorRect = useCallback((anchorId: string, fallback?: Element | null) => {
    return uiAnchorRegistry.getRect(anchorId) ?? fallback?.getBoundingClientRect();
  }, []);
  const getPreparedCardRect = useCallback((cardId: string) => {
    return uiAnchorRegistry.getRect(getPreparedCardAnchorId(cardId)) ?? preparedCardRefs.current[cardId]?.getBoundingClientRect();
  }, []);
  const getBoardCenter = useCallback(() => {
    const boardRect = uiAnchorRegistry.getRect(UI_ANCHOR_IDS.gameBoard) ?? uiAnchorRegistry.getRect(UI_ANCHOR_IDS.shellFrame);
    if (boardRect) {
      return {
        x: boardRect.left + boardRect.width / 2,
        y: boardRect.top + boardRect.height / 2,
      };
    }

    return {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
    };
  }, []);
  const [notifRefreshTick, setNotifRefreshTick] = useState(0);

  useEffect(() => {
    warmAllGameAssets((loaded, total) => {
      setLoadProgress(Math.round((loaded / total) * 100));
    }).then(() => {
      setAssetsLoaded(true);
    }).catch((error) => {
      console.error('[App] Asset warmup failed', error);
      setAssetsLoaded(true);
    });
  }, []);

  // Prevent browser context menu (right-click menu)
  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      return false;
    };

    document.addEventListener('contextmenu', handleContextMenu);
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  const [showConfirmDialog, setShowConfirmDialog] = useState({ visible: false, message: '', onConfirm: () => { } });

  useEffect(() => {
    setUiPack('classic')
  }, [])

  // --- Multiplayer State ---
  const [playerName, setPlayerName] = useState(() => auth?.username || '');
  const [multiplayerState, setMultiplayerState] = useState<MultiplayerState>({
    localPlayerId: null,
    localPlayerNumber: null,
    roomInfo: null,
    connectionStatus: 'DISCONNECTED',
    gameSeed: null,
  });
  const [wsError, setWsError] = useState<string | null>(null);
  const [playersReady, setPlayersReady] = useState<Set<number>>(new Set());
  const [lastOpponentAction, setLastOpponentAction] = useState<string | null>(null);
  const [aiIsReady, setAiIsReady] = useState(false);
  const aiStartRef = useRef<number | null>(null)
  const aiSubmittedRef = useRef<boolean>(false)
  const [availableRooms, setAvailableRooms] = useState<RoomListEntry[]>([]);
  const [chatMessages, setChatMessages] = useState<LobbyChatMessage[]>([]);

  const handleAuthDone = (next: { username: string; token?: string; opalstoneId?: string }) => {
    setAuth(next)
    setPlayerName(next.username)
    try { localStorage.setItem('oko_auth', JSON.stringify(next)) } catch { }
    // Opalstone ID gift notification (Trifle)
    if (next.opalstoneId) {
      const notifKey = `oko_notified_opalstone_${next.username}`;
      if (!localStorage.getItem(notifKey)) {
        addNotification(next.username, 'individual', 'notif.opalstoneGift', { hero: 'Trifle' });
        localStorage.setItem(notifKey, 'true');
        setNotifRefreshTick(t => t + 1);
        setShowTriffleUnlock(true);
      }
    }
    // Use React state instead of page reload to avoid issues with stale data
    setGameMode('MAIN_MENU')
  }

  useEffect(() => {
    const base = getApiBase()
    if (!auth?.token) { setGameMode('AUTH'); return }
    let canceled = false
    fetch(`${base}/api/me?token=${encodeURIComponent(auth.token)}`)
      .then(r => r.json())
      .then(data => {
        if (canceled) return
        if (!data || !data.ok) {
          console.warn('[App] Auth check returned error:', data)
          localStorage.removeItem('oko_auth')
          setAuth(null)
          setGameMode('AUTH')
        } else {
          // Sync heroes from server
          if (data.user && data.user.heroes) {
            const serverHeroes: string[] = data.user.heroes;
            // We need to map server hero names to IDs if they differ, but here they seem to be names or IDs?
            // Server sends names like 'Verisk', 'Nyrix'. Client uses IDs 'VERISK', 'NYRIX'.
            // Let's normalize.
            const normalized = serverHeroes.map(h => h.toUpperCase().replace(/[\s-]/g, '_') as HeroId);
            setAvailableHeroes(normalized);
          }
          if (data.user && data.user.emoteSlots) {
            setAuth(prev => prev ? { ...prev, emoteSlots: data.user.emoteSlots } : null);
          }
        }
      })
      .catch((e) => {
        if (canceled) return
        console.error('[App] Auth check failed:', e)
        localStorage.removeItem('oko_auth')
        setAuth(null)
        setGameMode('AUTH')
      })
    return () => { canceled = true }
  }, [auth?.token])

  const isMultiplayer = gameMode === 'PLAYING' && multiplayerState.localPlayerNumber !== null;
  const localPlayerId = isMultiplayer ? multiplayerState.localPlayerNumber! : 1;
  const opponentPlayerId = localPlayerId === 1 ? 2 : 1;
  const playMode: agents.PlayMode = isMultiplayer ? 'MULTIPLAYER_PVP' : 'SINGLE_PLAYER';
  const policy = getPlayPolicy(playMode);

  const sendDayReadySignal = () => {
    try { wsClient.send({ type: 'DAY_READY', round: gameState?.round }) } catch { }
  };

  // Action hints for wave signal display
  const { hints: actionHints, getHint, isEnabled: hintsEnabled } = useActionHints(gameState, localPlayerId);

  // Sync targeting card reference with game state (handling rotation updates)
  useEffect(() => {
    (window as any).okoGamePhase = gameState?.phase;
  }, [gameState?.phase]);

  // IMPORTANT: Preserve rotation from user's mode choice (e.g., Protector ATTACK mode)
  useEffect(() => {
    if (targeting && gameState) {
      const player = gameState.players.find(p => p.id === localPlayerId);
      const currentCard = player?.prepared.find(p => p.card.id === targeting.card.card.id);
      if (currentCard && currentCard !== targeting.card) {
        // Preserve the rotation from the targeting state, don't use gameState's rotation
        const preservedRotation = targeting.card.rotation;
        const updatedCard = { ...currentCard, rotation: preservedRotation };
        setTargeting(prev => prev ? ({ ...prev, card: updatedCard }) : null);
      }
    }
  }, [gameState, targeting, localPlayerId]);

  // Hand scrolling logic (touch, wheel, animation loop)
  const {
    handScroll,
    setHandScroll,
    updateScrollSpeed,
    handleBackgroundMouseDown,
    handleBackgroundMouseUp,
  } = useHandScroll({
    gameState,
    localPlayerId,
    handContainerRef,
  });

  // Touch-based hand scrolling for mobile devices (single-finger drag)
  useTouchHandScroll({
    containerRef: handContainerRef,
    onScrollUpdate: (delta) => setHandScroll(prev => prev + delta),
    enabled: isTouchDevice() && gameState?.phase === GamePhase.Preparation,
  });

  useRegisterUiAnchor(UI_ANCHOR_IDS.localAvatar, playerAvatarRef, { kind: 'avatar', ownerId: localPlayerId }, [
    localPlayerId,
    gameState?.phase,
    gameState?.round,
  ]);
  useRegisterUiAnchor(UI_ANCHOR_IDS.opponentAvatar, aiAvatarRef, { kind: 'avatar' }, [
    localPlayerId,
    gameState?.phase,
    gameState?.round,
  ]);
  useRegisterUiAnchor(UI_ANCHOR_IDS.deckZone, deckZoneRef, { kind: 'deck-zone' }, [
    gameState?.phase,
    gameState?.round,
    isDealing,
  ]);
  useRegisterUiAnchor(UI_ANCHOR_IDS.prepZone, prepZoneRef, { kind: 'prep-zone', ownerId: localPlayerId }, [
    localPlayerId,
    gameState?.phase,
    gameState?.round,
    handLayoutTick,
  ]);
  useRegisterUiAnchor(UI_ANCHOR_IDS.handRail, handContainerRef, { kind: 'hand-rail', ownerId: localPlayerId }, [
    localPlayerId,
    gameState?.phase,
    handLayoutTick,
    handScroll,
  ]);

  useEffect(() => {
    const nextPreparedAnchorIds = new Set<string>();

    Object.entries(preparedCardRefs.current).forEach(([cardId, element]) => {
      if (!element) return;
      const anchorId = getPreparedCardAnchorId(cardId);
      uiAnchorRegistry.updateFromElement(anchorId, element, { kind: 'prepared-card', ownerId: element.id.includes('-me-') ? localPlayerId : 'opponent' });
      nextPreparedAnchorIds.add(anchorId);
    });

    preparedAnchorIdsRef.current.forEach((anchorId) => {
      if (!nextPreparedAnchorIds.has(anchorId)) {
        uiAnchorRegistry.unregister(anchorId);
      }
    });

    preparedAnchorIdsRef.current = nextPreparedAnchorIds;
    return () => {
      nextPreparedAnchorIds.forEach((anchorId) => uiAnchorRegistry.unregister(anchorId));
    };
  }, [gameState, handLayoutTick, localPlayerId]);


  // ML: Start recording when game starts (single-player vs AI only)
  // Match recording logic was here

  // ML: End recording logic was here

  // Report match result to server (multiplayer only)
  const matchReportedRef = useRef(false);
  useEffect(() => {
    if (gameState?.phase === GamePhase.GameOver && isMultiplayer && auth?.token && !matchReportedRef.current) {
      // Technical victories are already reported by the signaling server
      if (gameState.pendingVictory?.isTechnical) {
        matchReportedRef.current = true;
        return;
      }
      matchReportedRef.current = true;
      const isWin = gameState.winner?.id === localPlayerId;
      const isDraw = !gameState.winner;
      const result = isDraw ? 'draw' : isWin ? 'win' : 'loss';
      const opponent = gameState.players.find(p => p.id !== localPlayerId);
      const myPlayer = gameState.players.find(p => p.id === localPlayerId);
      const fameDelta = isWin ? (gameState.lastFameDelta || 0) : 0;
      const matchId = `${gameState.seed}-${gameState.round}-${gameState.winner?.id ?? 'draw'}`;

      fetch(`${getApiBase()}/api/report-match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          matchId,
          token: auth.token,
          opponentName: opponent?.name || 'Unknown',
          result,
          fameDelta,
          myHero: myPlayer?.hero,
          myClass: myPlayer?.playerClass,
          opponentHero: opponent?.hero,
          opponentClass: opponent?.playerClass
        })
      })
        .then(r => r.json())
        .then(d => {
          if (d.ok) console.log(`[Fame] Match reported: ${result}, new fame: ${d.fame}`);
          else console.warn('[Fame] Failed to report match:', d.error);
        })
        .catch(e => console.error('[Fame] Failed to report match:', e));
    }
    if (gameState?.phase !== GamePhase.GameOver) {
      matchReportedRef.current = false;
    }
  }, [gameState?.phase, gameState?.winner, isMultiplayer, auth?.token, localPlayerId]);

  const currentActivation = gameState ? (
    (gameState.activations?.length ?? 0) > 0
      ? (gameState.activations.filter(a => !a.accepted).pop() || gameState.activations[gameState.activations.length - 1])
      : null
  ) : null;

  const {
    hintVisible,
    hintTitle,
    hintLines,
    hintIcons,
    idleHintVisible,
    idleHintTitle,
    idleHintLines,
    handleCardMouseEnterApp,
    handleCardMouseLeaveApp,
    markAction,
    dismissIdleHint,
    handleCloseReactionHint,
    maskingDelay,
  } = useHints({ gameState, localPlayerId, playerIsReady, currentActivation, language, isPaused: showInGameMenu });
  const lastInstructorPauseDebugRef = useRef<string>('');
  useEffect(() => {
    const snapshot = JSON.stringify({
      round: gameState?.round ?? null,
      phase: gameState?.phase ?? null,
      turnPlayerId: gameState?.turnPlayerId ?? null,
      showInGameMenu,
      idleHintVisible,
      draggedCardInfo: !!draggedCardInfo,
      targeting: !!targeting,
      instructorPaused: showInGameMenu || !!draggedCardInfo || !!targeting,
      currentActivationLength: gameState?.activations?.length ?? 0,
    });
    if (snapshot !== lastInstructorPauseDebugRef.current) {
      lastInstructorPauseDebugRef.current = snapshot;
      console.log('[App][InstructorPause][debug]', JSON.parse(snapshot));
    }
  }, [gameState?.round, gameState?.phase, gameState?.turnPlayerId, showInGameMenu, idleHintVisible, draggedCardInfo, targeting, gameState?.activations?.length]);
  const [mageModalOpen, setMageModalOpen] = useState(false);
  const [mageLaneYPos, setMageLaneYPos] = useState(0);
  useEffect(() => { setMageLaneYPos(mageModalOpen ? 1 : 0); }, [mageModalOpen]);
  useEffect(() => {
    if (gameState?.phase === GamePhase.Preparation) {
      setMageModalOpen(false);
      setPlayerIsReady(false);
      setPlayersReady(new Set()); // Reset stale DAY_READY state from previous round
    }
  }, [gameState?.phase]);

  useEffect(() => {
    if (!isMultiplayer || playersReady.size !== 2) return;
    setPlayersReady(new Set());
  }, [playersReady, isMultiplayer]);

  useEffect(() => {
    if (!isMultiplayer || !gameState || gameState.phase !== GamePhase.Preparation) return;

    const willSkipNight = (gameState.pendingExtraDayCount || 0) > 0;

    if (willSkipNight) {
      if (!playersReady.has(localPlayerId)) {
        setPlayersReady(prev => {
          const s = new Set(prev);
          s.add(localPlayerId);
          return s;
        });
        sendDayReadySignal();
      }
      return;
    }

    if (!playerIsReady) return;
    if (playersReady.has(opponentPlayerId)) return;

    sendDayReadySignal();
    const interval = setInterval(() => {
      sendDayReadySignal();
    }, 1500);

    return () => clearInterval(interval);
  }, [isMultiplayer, gameState?.phase, gameState?.pendingExtraDayCount, playerIsReady, playersReady, opponentPlayerId, localPlayerId]);

  // Callback to reset all drag-related state when returning to menu or starting new game
  const resetDragState = useCallback(() => {
    setDraggedCardInfo(null);
    setTargeting(null);
    setShowDefenseGhost(false);
    setHandGapIndex(null);
    setHandScroll(0);
    setAiIsReady(false);
    setPlayersReady(new Set());
  }, []);

  const { toggleInGameMenu, handleNavigate, handleStartGame, handleRestart, handleBack } = useNavigation({
    gameMode,
    lastGameMode,
    setGameMode,
    setLastGameMode,
    setWsError,
    setShowInGameMenu,
    setGameState,
    setPlayerIsReady,
    setAnimations,
    resetDragState,
  });

  const { createRoom, joinRoom, leaveRoom, toggleReady, startGame, requestRoomList, sendChatMessage } = useMultiplayer(
    gameMode,
    gameState,
    applyGameAction,
    (u) => setGameState(u),
    (u) => setMultiplayerState(u),
    (v) => setWsError(v),
    (v) => setLastOpponentAction(v),
    (mode) => handleNavigate(mode),
    (playerNumber) => {
      setGameState(currentState => {
        if (!currentState || currentState.phase !== GamePhase.Preparation) {
          console.log(`[App] Ignoring stale DAY_READY (phase: ${currentState?.phase})`);
          return currentState;
        }
        setPlayersReady(prev => {
          const s = new Set(prev)
          s.add(playerNumber)
          return s
        })
        return currentState
      })
    },
    (rooms) => setAvailableRooms(rooms),
    (msg) => setChatMessages(prev => [...prev, msg]),
  );





  const executeGameAction = useCallback((action: GameAction, localUpdate: (state: GameState) => GameState) => {
    if (isMultiplayer) {
      if (multiplayerState.connectionStatus !== 'CONNECTED') {
        setWsError('Not connected to server. Please check your connection.');
        setTimeout(() => setWsError(null), 3100);
        console.error('[APP] Attempted to send action while not connected:', action.type);
        return;
      }

      const actionId = `P${localPlayerId}-${action.type}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      wsClient.sendReliable({
        type: 'GAME_ACTION_V2',
        actionId,
        sentAt: Date.now(),
        attempt: 1,
        action,
      }).then(ack => {
        console.log(`[App] Server accepted action:`, action.type, actionId, `stateVersion=${ack.stateVersion ?? 'unknown'}`);
      }).catch(err => {
        console.error(`[App] Authoritative send failed for ${action.type}:`, err);
        setWsError('Action was not confirmed by the server. Restoring latest match state.');
        setTimeout(() => setWsError(null), 4000);
        try {
          wsClient.send({ type: 'REQUEST_LATEST_STATE' });
        } catch { }
      });
      console.log(`[App] Sending authoritative action via server:`, action.type, actionId);
      setGameState(prevState => prevState ? localUpdate(prevState) : null)
    } else {
      // In single-player: update immediately
      setGameState(prevState => prevState ? localUpdate(prevState) : null);
    }
  }, [isMultiplayer, multiplayerState.connectionStatus, gameState, localPlayerId]);



  const handleWsCreateRoom = (_pName: string, roomName: string, password?: string) => {
    createRoom(playerName, roomName, password, auth?.token);
  };

  const handleWsJoinRoom = (roomCode: string, _pName: string, password?: string) => {
    joinRoom(roomCode, playerName, password, auth?.token);
  };

  const handleWsLeaveRoom = () => {
    leaveRoom(true);
    setMultiplayerState(prev => ({ ...prev, roomInfo: null }));
    handleNavigate('MULTIPLAYER_MENU');
  };

  // Lobby Switching Logic
  const handleSwitchRoom = async (room: RoomListEntry) => {
    if (isSwitchingRoom) return;
    setIsSwitchingRoom(true);

    // 1. Leave current room
    leaveRoom(true); // Use the raw hook function, not handleWsLeaveRoom which navigates away!
    // We manually update state to null roomInfo to satisfy UI check, but we DO NOT navigate
    setMultiplayerState(prev => ({ ...prev, roomInfo: null }));

    // 2. Show toast
    setToastMessage(t('ui.switchingRoom', language));
    setToastType('info');

    // 3. Robust Sequence: Wait for "Leave" to propagate or timeout
    await new Promise(resolve => setTimeout(resolve, 600));

    // 4. Join new room
    if (room.isLocked) {
      handleWsJoinRoom(room.code, playerName, '');
    } else {
      handleWsJoinRoom(room.code, playerName);
    }

    // Reset switching flag
    setTimeout(() => {
      setIsSwitchingRoom(false);
    }, 2000);
  };

  const handleWsToggleReady = () => {
    toggleReady();
  };

  const handleWsStartGame = () => {
    startGame();
  };



  const handleQuit = () => {
    // Navigate to login instead of closing tab
    try { localStorage.removeItem('oko_auth') } catch { }
    setAuth(null)
    setGameMode('AUTH')
  };



  const confirm = useConfirm({
    showConfirmDialog,
    setShowConfirmDialog,
    setShowInGameMenu,
    setGameState,
    setGameMode,
    handleQuit,
    resetDragState,
    // Multiplayer cleanup functions
    leaveRoom,
    closeWebRTC: () => webrtcClient.close(),
    setMultiplayerState: (v) => setMultiplayerState(v),
    language,
    isMultiplayer,
    gameState,
  });

  useEffect(() => {
    // Cache the rects of currently mounted prepared cards to support animations after unmount
    Object.entries(preparedCardRefs.current).forEach(([id, el]) => {
      if (el && el instanceof HTMLDivElement) {
        lastKnownCardRects.current[id] = el.getBoundingClientRect();
      }
    });
  });

  useEffect(() => {
    if (!prevGameState || !gameState) return;
    if (!prevGameState.activations || !gameState.activations) return;
    if (prevGameState.activations.length > 0 && gameState.activations.length < prevGameState.activations.length) {
      setActivationResolved(true);
    }
    const targets: AnimationTargets = {
      playerAvatar: getAnchorRect(UI_ANCHOR_IDS.localAvatar, playerAvatarRef.current),
      aiAvatar: getAnchorRect(UI_ANCHOR_IDS.opponentAvatar, aiAvatarRef.current),
      deckZone: getAnchorRect(UI_ANCHOR_IDS.deckZone, deckZoneRef.current),
      localPlayerId,
    };
    const newAnimations = computeNewAnimations(prevGameState, gameState, preparedCardRefs.current, effectCardRefs.current, targets, lastKnownCardRects.current)
    if (newAnimations.length > 0) setAnimations(prev => [...prev, ...newAnimations]);
  }, [gameState, getAnchorRect, localPlayerId, prevGameState]);

  useEffect(() => {
    if (!gameState) return
    const isSingle = playMode === 'SINGLE_PLAYER'
    if (!isSingle) return
    if (gameState.phase === GamePhase.GameOver && gameState.winner && gameState.winner.id === 1 && !aiSubmittedRef.current) {
      const start = aiStartRef.current || Date.now()
      const elapsedSec = Math.max(0, Math.floor((Date.now() - start) / 1000))
      aiSubmittedRef.current = true
      const base = getApiBase()
      const token = auth?.token
      if (token) {
        try { fetch(`${base}/api/ai-win`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, durationSec: elapsedSec }) }) } catch { }
      }
    }
  }, [gameState?.phase])

  useGameEffects(gameState, localPlayerId, playerAvatarRef, aiAvatarRef);

  // Mug animation controller - handles Cheers/TakePut animations during Night phase
  const mugController = useMugAnimationController(gameState, localPlayerId, cheersPlaying);

  useEffect(() => {
    if (gameState?.revealedCard) {
      const timer = setTimeout(() => {
        setGameState(prev => prev ? { ...prev, revealedCard: null } : null);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [gameState?.revealedCard]);

  useBrokenSecret(gameState, localPlayerId, executeGameAction);

  useEffect(() => {
    const newRotations = cleanupHandRotations(gameState, localPlayerId, handCardRotations)
    if (newRotations !== handCardRotations) setHandCardRotations(newRotations)
  }, [gameState?.players, localPlayerId])

  const handleAnimationEnd = (id: string) => setAnimations(prev => prev.filter(anim => anim.id !== id));
  const isCardAnimating = (cardId: string) => animations.some(a => a.card.id === cardId) || flourishAnimations.some(a => a.card.id === cardId);

  // Flourish animation handlers
  const handleFlourishEnd = useCallback((id: string) => {
    setFlourishAnimations(prev => prev.filter(a => a.id !== id));
  }, []);

  const handleFlourishTrigger = useCallback((cardId: string, rect: DOMRect, rotation: number, speed: number) => {
    const card = gameState?.players.flatMap(p => [...p.hand, ...p.prepared.map(pc => pc.card)]).find(c => c.id === cardId);
    if (!card) return;
    setFlourishAnimations(prev => [...prev, {
      id: `flourish-${cardId}-${Date.now()}`,
      card,
      startRect: rect,
      rotation,
      rotationSpeed: speed
    }]);
  }, [gameState]);


























  const handleChooseTower = (towerColor: CardColor) => uiActions.handleChooseTower(towerColor);

  const handleStashChoice = (cardId: string, fromBurned?: boolean) => {
    if (!gameState || gameState.phase !== GamePhase.StashChoice) return;
    const opponentId = isMultiplayer
      ? multiplayerState.roomInfo?.players.find(p => p.id !== multiplayerState.localPlayerId)?.id
      : 2;

    executeGameAction(
      { type: 'CHOOSE_STASH', playerId: localPlayerId, cardId, fromBurned },
      (state) => {
        return gameLogic.makeStashChoice(state, localPlayerId, cardId, fromBurned);
      }
    );
  };

  const handleEndDay = () => {
    markAction();
    if (!gameState || gameState.phase !== GamePhase.Preparation || playerIsReady) return;
    const willSkipNight = (gameState.pendingExtraDayCount || 0) > 0;
    if (!willSkipNight) {
      setPlayerIsReady(true);
    }

    if (isMultiplayer) {
      if (willSkipNight) {
        setPlayersReady(prev => {
          const s = new Set(prev);
          s.add(localPlayerId);
          return s;
        });
        sendDayReadySignal();
      } else {
        setPlayersReady(prev => {
          const s = new Set(prev);
          s.add(localPlayerId);
          return s;
        });
        sendDayReadySignal();
      }
    } else {
      // Single-player: use functional update to get latest state
      if (!aiIsReady) {
        setGameState(prev => {
          if (!prev) return prev;
          const s1 = gameLogic.runAIDayActions(prev);
          const s2 = gameLogic.runAIPreparationTurn(s1);
          return s2.newState;
        });
        setAiIsReady(true);
      }
      setTimeout(() => {
        setGameState(prev => prev ? gameLogic.startCombat(prev) : prev);
      }, 1000);
    }
  };

  const handleActivateCard = useCallback((cardId: string, rotation: number, targetId?: number) => {
    console.log(`[handleActivateCard DEBUG] cardId=${cardId}, rotation=${rotation}, targetId=${targetId}`);
    markAction();
    if (!gameState || gameState.phase !== GamePhase.Combat || currentActivation || gameState.discardMode?.[localPlayerId]) return;
    if (gameState.turnPlayerId !== localPlayerId) return;

    // Trigger animation if it's an attack card
    // Animation is handled by useEffect on game state change

    executeGameAction(
      { type: 'ATTEMPT_ACTIVATION', playerId: localPlayerId, cardId, rotation, targetId },
      (state) => gameLogic.attemptActivation(state, localPlayerId, cardId, rotation, targetId)
    );
  }, [gameState, isMultiplayer, localPlayerId, executeGameAction, currentActivation]);

  const uiActions = useUiActions({
    gameState,
    setGameState,
    localPlayerId,
    executeGameAction,
    preparedCardRefs,
    setTargeting,
    handleActivateCard,
    setProtectorChoiceCardId,
    playerIsReady,
  });

  const isExtraDay = !!gameState && gameState.phase === GamePhase.Preparation && ((gameState.pendingExtraDayCount || 0) > 0);





  const interactions = useBoardInteractions({
    gameState,
    setGameState,
    localPlayerId,
    playerIsReady: playerIsReady && !isExtraDay,
    executeGameAction,
    groupHandForDisplay,
    currentActivation,
    setDraggedCardInfo,
    setShowDefenseGhost,
    setHandCardRotations,
    draggedCardInfo,
    setTargeting,
    setMousePos,
    targeting,
    aiAvatarRef,
    playerAvatarRef,
    handleActivateCard,
    handleMageCastFromHandWithTarget: uiActions.handleMageCastFromHandWithTarget,
    onMageCastSuccess: () => setMageModalOpen(false),
    activationZonesRef,
    setProtectorChoiceCardId,
    handleAstridBlastWithTarget: uiActions.handleAstridBlastWithTarget,
  });
  const {
    handleCounterDragEnter,
    handleCounterDragLeave,
    handleTargetableMouseDown,
    handlePassTurn,
    handleAcceptActivation,
    handleToggleDiscardMode,
    handleDayDiscard,
    handleDayPreparedDiscard,
    handleNightDiscard,
    handleCardDragStart,
    handleCardDragEnd,
    handleDragOver,
    handleDropOnHand,
    handleDropOnActivationChain,
    handleDropOnPrep,
    handlePrepareCard,
    handlePreparedCardRotation,
    handleHandCardRotation,
    handleReturnCardToHand,
    handleReorderHand,
  } = interactions;










  const handleDragEndAppWrapped = useCallback(() => {
    handleCardDragEnd();
    setHandGapIndex(null);
  }, [handleCardDragEnd]);

  const handleDropOnPrepWrapped = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    handleDropOnPrep(e);
    handleCardDragEnd(); // Force clear drag state since Card might unmount
    setHandGapIndex(null);
    markAction();
  }, [handleDropOnPrep, handleCardDragEnd, markAction]);

  // Touch-based card preparation: check if drop coordinates are over prep zone or prepared cards
  const handleTouchDrop = useCallback((card: CardData, rotation: number, dropX: number, dropY: number) => {
    console.log('[App] Touch drop:', { cardId: card.id, rotation, dropX, dropY });

    const player = gameState?.players.find(p => p.id === localPlayerId);
    const preparedCard = player?.prepared.find(pc => pc.card.id === card.id);
    const normalizedRotation = Math.round(rotation / 180) * 180;

    // Calculate if it's player's turn to respond (for counter cards)
    const localIsMyTurnToRespond = gameState?.activations?.some(act =>
      !act.resolved && !act.accepted && (
        (act.state === 'PENDING_RESPONSE' && act.initiatorId !== localPlayerId) ||
        (act.state === 'PENDING_CONFIRMATION' && act.initiatorId === localPlayerId)
      )
    ) ?? false;

    // NEW: Check if dropped on activation chain zone (for counter cards like Psy-Block, Defense, Shield)
    if (localIsMyTurnToRespond && preparedCard && gameState) {
      for (const zone of activationZonesRef.current) {
        // Hit-test against activation zone (with some expanded tolerance)
        const tolerance = 20; // Add some tolerance for easier touch targeting
        if (dropX >= zone.rect.left - tolerance && dropX <= zone.rect.right + tolerance &&
          dropY >= zone.rect.top - tolerance && dropY <= zone.rect.bottom + tolerance) {
          console.log('[App] Touch drop on activation zone:', zone.activationId, 'with card:', card.id);
          handleCardDragEnd(); // Force clear drag state
          markAction();

          // Check if this is a shield/extended shield card or a counter card
          const hasExtendedShield = !!preparedCard.card.extendedShield;
          const isBurglarySpell = preparedCard.card.type === CardType.Steal && (preparedCard.card as any).subType === 'Spell';

          if (hasExtendedShield && !isBurglarySpell) {
            // Shield activation (Protector, Planted Bomb, etc.)
            console.log('[App] Touch drop - activating shield on activation:', zone.activationId);
            interactions.handleShieldActivation(card.id, normalizedRotation, zone.activationId);
          } else {
            // Counter activation (Psy-Block, Defense, etc.)
            console.log('[App] Touch drop - countering activation:', zone.activationId);
            interactions.handleCounterActivation(card.id, normalizedRotation, zone.activationId);
          }

          handleCardDragEnd();
          setHandGapIndex(null);
          return;
        }
      }
    }

    // Check if this is a targetable card from prepared area
    if (preparedCard && gameState && gameState.phase === GamePhase.Combat && gameState.turnPlayerId === localPlayerId && !currentActivation) {
      const effect = gameLogic.getCardEffect(preparedCard);
      const isTargetable = gameLogic.isTargetableEffect(effect.type);

      if (isTargetable) {
        // Check if dropped on opponent avatar
        const aiRect = getAnchorRect(UI_ANCHOR_IDS.opponentAvatar, aiAvatarRef.current);
        if (aiRect && dropX >= aiRect.left && dropX <= aiRect.right && dropY >= aiRect.top && dropY <= aiRect.bottom) {
          console.log('[App] Touch drop on opponent avatar - activating card');
          const opponentId = gameState.players.find(p => p.id !== localPlayerId)?.id;
          if (opponentId) {
            handleActivateCard(card.id, normalizedRotation, opponentId);
            handleCardDragEnd();
            setHandGapIndex(null);
            return;
          }
        }

        // Check if dropped on player avatar (for Heal targeting self)
        const playerRect = getAnchorRect(UI_ANCHOR_IDS.localAvatar, playerAvatarRef.current);
        if (playerRect && dropX >= playerRect.left && dropX <= playerRect.right && dropY >= playerRect.top && dropY <= playerRect.bottom) {
          console.log('[App] Touch drop on player avatar - activating card');
          handleActivateCard(card.id, normalizedRotation, localPlayerId);
          handleCardDragEnd();
          setHandGapIndex(null);
          return;
        }
      }
    }

    // Check if drop is over any prepared card (for activation chain targeting - legacy desktop fallback)
    for (const [prepCardId] of Object.entries(preparedCardRefs.current)) {
      const rect = getPreparedCardRect(prepCardId);
      if (!rect) continue;
      if (dropX >= rect.left && dropX <= rect.right && dropY >= rect.top && dropY <= rect.bottom) {
        console.log('[App] Touch drop on prepared card:', prepCardId);
        markAction();
        // Simulate a drop event for activation chain - call the underlying handler directly
        // We need to set dragging card data first
        if (draggedCardInfo) {
          handleDropOnActivationChain(
            { preventDefault: () => { }, dataTransfer: { getData: (key: string) => key === 'cardId' ? card.id : (key === 'rotation' ? String(normalizedRotation) : (key === 'source' ? 'prepared' : 'hand')) } } as unknown as React.DragEvent<HTMLDivElement>,
            prepCardId
          );
        }
        handleCardDragEnd();
        setHandGapIndex(null);
        return;
      }
    }

    // Then check prep zone
    const prepZoneRect = getAnchorRect(UI_ANCHOR_IDS.prepZone, prepZoneRef.current);
    if (prepZoneRect) {
      const rect = prepZoneRect;
      const isOverPrep = dropX >= rect.left && dropX <= rect.right && dropY >= rect.top && dropY <= rect.bottom;
      console.log('[App] Touch drop prep zone check:', { isOverPrep, rect });
      if (isOverPrep) {
        markAction();
        handlePrepareCard(card.id, normalizedRotation);
      }
    }

    handleCardDragEnd();
    setHandGapIndex(null);
  }, [gameState, localPlayerId, currentActivation, handleActivateCard, handlePrepareCard, handleCardDragEnd, markAction, draggedCardInfo, handleDropOnActivationChain, interactions, getAnchorRect, getPreparedCardRect]);


  const handleHandContainerDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const cardId = e.dataTransfer.getData('cardId');
    const source = e.dataTransfer.getData('source');
    if (!cardId) return;
    const clientX = (e as any).clientX;
    logSpace('hand.drop.start', { source, cardId, clientX });
    handleCardDragEnd();
    setHandGapIndex(null);
    suppressGapRef.current = true;
    if (!gameState) return;
    const playerIndex = gameState.players.findIndex(p => p.id === localPlayerId);
    if (playerIndex === -1) return;

    const containerRect = getAnchorRect(UI_ANCHOR_IDS.handRail, handContainerRef.current);
    const centerX = containerRect ? (containerRect.left + containerRect.width / 2) : getBoardCenter().x;

    if (source === 'prepared') {
      handleReturnCardToHand(cardId);
      setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          if (isMultiplayer) return prev;
          const pIndex = prev.players.findIndex(p => p.id === localPlayerId);
          if (pIndex === -1) return prev;
          const playerPrev = prev.players[pIndex];
          const grouped = groupHandForDisplay(playerPrev.hand);
          const currentIdx = grouped.findIndex(g => g.cards.some(c => c.id === cardId));
          if (currentIdx === -1) return prev;
          const cardCountAfter = grouped.length;
          let rawTarget = 0;
          if (typeof handGapIndex === 'number') {
            rawTarget = Math.max(0, Math.min(handGapIndex, cardCountAfter - 1));
          } else {
            const x = clientX - centerX - handScroll;
            const idxFloat = x / 110 + ((cardCountAfter - 1) / 2);
            rawTarget = Math.max(0, Math.min(Math.round(idxFloat), cardCountAfter - 1));
          }
          const adjTarget = Math.max(0, Math.min(rawTarget - (currentIdx < rawTarget ? 1 : 0), cardCountAfter - 1));
          logSpace('hand.drop.prepared.reorder', { currentIdx, rawTarget, adjTarget, keys: grouped.map(g => g.key) });
          const newGrouped = [...grouped];
          const [moved] = newGrouped.splice(currentIdx, 1);
          newGrouped.splice(adjTarget, 0, moved);
          const newHand = newGrouped.flatMap(g => g.cards);
          const newPlayers = [...prev.players] as [Player, Player];
          newPlayers[pIndex] = { ...playerPrev, hand: newHand };
          return { ...prev, players: newPlayers };
        });
        setHandScroll(0);
        setHandLayoutTick(t => t + 1);
        logSpace('hand.drop.end', { source: 'prepared' });
        setHandGapIndex(null);
        suppressGapRef.current = false;
      }, 0);
    } else if (source === 'hand') {
      setGameState(prev => {
        if (!prev) return prev;
        if (isMultiplayer) return prev;
        const pIndex = prev.players.findIndex(p => p.id === localPlayerId);
        if (pIndex === -1) return prev;
        const playerPrev = prev.players[pIndex];
        const grouped = groupHandForDisplay(playerPrev.hand);
        const currentIdx = grouped.findIndex(g => g.cards.some(c => c.id === cardId));
        if (currentIdx === -1) return prev;
        const cardCountAfter = grouped.length;
        let rawTarget = 0;
        if (typeof handGapIndex === 'number') {
          rawTarget = Math.max(0, Math.min(handGapIndex, cardCountAfter - 1));
        } else {
          const x = clientX - centerX - handScroll;
          const idxFloat = x / 110 + ((cardCountAfter - 1) / 2);
          rawTarget = Math.max(0, Math.min(Math.round(idxFloat), cardCountAfter - 1));
        }
        const adjTarget = Math.max(0, Math.min(rawTarget - (currentIdx < rawTarget ? 1 : 0), cardCountAfter - 1));
        logSpace('hand.drop.hand.reorder', { currentIdx, rawTarget, adjTarget, keys: grouped.map(g => g.key) });
        const newGrouped = [...grouped];
        const [moved] = newGrouped.splice(currentIdx, 1);
        newGrouped.splice(adjTarget, 0, moved);
        const newHand = newGrouped.flatMap(g => g.cards);
        const newPlayers = [...prev.players] as [Player, Player];
        newPlayers[pIndex] = { ...playerPrev, hand: newHand };
        return { ...prev, players: newPlayers };
      });
      setHandScroll(0);
      setHandLayoutTick(t => t + 1);
      logSpace('hand.drop.end', { source: 'hand' });
      setHandGapIndex(null);
    }
  }, [gameState, localPlayerId, handScroll, setGameState, handleReturnCardToHand, groupHandForDisplay, handGapIndex, getAnchorRect, getBoardCenter]);

  const handlePreparedDoubleClick = useCallback((e: React.MouseEvent<HTMLDivElement>, cardId: string) => {
    if (!gameState || gameState.phase !== GamePhase.Preparation || (playerIsReady && !isExtraDay)) return;

    // In day discard mode, discard prepared color cards for essence instead of returning to hand
    if (gameState.discardMode?.[localPlayerId] === 'day') {
      const player = gameState.players.find(p => p.id === localPlayerId)!;
      const preparedCard = player.prepared.find(pc => pc.card.id === cardId);
      if (preparedCard && !preparedCard.card.essenceCost) {
        // It's a color card - discard for essence
        handleDayPreparedDiscard(cardId);
        return;
      }
    }

    handleCardDragEnd();
    setHandGapIndex(null);
    suppressGapRef.current = true;
    handleReturnCardToHand(cardId);
    const clientX = (e as any).clientX;
    const containerRect = getAnchorRect(UI_ANCHOR_IDS.handRail, handContainerRef.current);
    const centerX = containerRect ? (containerRect.left + containerRect.width / 2) : getBoardCenter().x;
    logSpace('prepared.dblclick.start', { cardId, clientX });
    setTimeout(() => {
      setGameState(prev => {
        if (!prev) return prev;
        if (isMultiplayer) return prev;
        const pIndex = prev.players.findIndex(p => p.id === localPlayerId);
        if (pIndex === -1) return prev;
        const playerPrev = prev.players[pIndex];
        const grouped = groupHandForDisplay(playerPrev.hand);
        const currentIdx = grouped.findIndex(g => g.cards.some(c => c.id === cardId));
        if (currentIdx === -1) return prev;
        const cardCountAfter = grouped.length;
        const x = clientX - centerX - handScroll;
        const idxFloat = x / 110 + ((cardCountAfter - 1) / 2);
        const rawTarget = Math.max(0, Math.min(Math.round(idxFloat), cardCountAfter - 1));
        const adjTarget = Math.max(0, Math.min(rawTarget - (currentIdx < rawTarget ? 1 : 0), cardCountAfter - 1));
        logSpace('prepared.dblclick.reorder', { currentIdx, rawTarget, adjTarget, keys: grouped.map(g => g.key) });
        const newGrouped = [...grouped];
        const [moved] = newGrouped.splice(currentIdx, 1);
        newGrouped.splice(adjTarget, 0, moved);
        const newHand = newGrouped.flatMap(g => g.cards);
        const newPlayers = [...prev.players] as [Player, Player];
        newPlayers[pIndex] = { ...playerPrev, hand: newHand };
        return { ...prev, players: newPlayers };
      });
      setHandScroll(0);
      setHandLayoutTick(t => t + 1);
      logSpace('prepared.dblclick.end', {});
      suppressGapRef.current = false;
    }, 0);
  }, [gameState, playerIsReady, localPlayerId, handScroll, setGameState, handleReturnCardToHand, groupHandForDisplay, getAnchorRect, getBoardCenter]);












  const handleStartTowerPurchase = () => {
    if (!gameState || gameState.phase !== GamePhase.Combat || gameState.turnPlayerId !== localPlayerId) return;
    const player = gameState.players.find(p => p.id === localPlayerId)!;
    if (player.capturedTowerThisTurn) {
      setToastMessage(t('log.towerFizzle', language).replace('{playerName}', player.name));
      setToastType('error');
      return;
    }
    executeGameAction(
      { type: 'START_TOWER_PURCHASE', playerId: localPlayerId },
      (state) => gameLogic.startTowerPurchase(state)
    );
  };

  const handleCancelTowerPurchase = () => {
    if (!gameState || gameState.phase !== GamePhase.TowerPurchase || gameState.turnPlayerId !== localPlayerId) return;
    executeGameAction(
      { type: 'CANCEL_TOWER_PURCHASE', playerId: localPlayerId },
      (state) => gameLogic.cancelTowerPurchase(state)
    );
  };

  const handlePurchaseTower = (color: CardColor) => uiActions.handlePurchaseTower(color);

  const handleChooseChromabowColor = (color: CardColor) => uiActions.handleChooseChromabowColor(color);

  const handleChooseHibernationColor = (color: CardColor) => uiActions.handleChooseHibernationColor(color);
  const handleChooseHeroClass = (hero: 'VERISK', playerClass: 'WARRIOR') => uiActions.handleChooseHeroClass(hero, playerClass);

  const handleProtectorModeChosen = (mode: 'ATTACK' | 'DEFENSE') => {
    if (!protectorChoiceCardId) return;
    const cardId = protectorChoiceCardId;
    setProtectorChoiceCardId(null);
    uiActions.handleProtectorModeChosen(cardId, mode);
    if (mode === 'ATTACK') {
      const oppAvatar = getAnchorRect(UI_ANCHOR_IDS.opponentAvatar, aiAvatarRef.current);
      if (oppAvatar && oppAvatar.width > 0) {
        setMousePos({ x: oppAvatar.left + oppAvatar.width / 2, y: oppAvatar.top + oppAvatar.height / 2 });
      } else {
        setMousePos(getBoardCenter());
      }
    }
  };

  const handleChoosePurificationTarget = (color: CardColor) => uiActions.handleChoosePurificationTarget(color);

  const handleReturnLockedCardFromOverflow = (cardId: string) => uiActions.handleReturnLockedCardFromOverflow(cardId);

  const handleOpenTowerChangeMenu = (towerIndex: number) => uiActions.handleOpenTowerChangeMenu(towerIndex);

  const handleCloseTowerChangeMenu = () => uiActions.handleCloseTowerChangeMenu();

  const handleChangeTowerColor = (newColor: CardColor) => uiActions.handleChangeTowerColor(newColor);

  useEffect(() => {
    if (isMultiplayer) return;
    if (!gameState || gameState.phase !== GamePhase.TowerChoice) return;
    const agentMap = agents.getAgentMap(gameState, playMode, localPlayerId);
    const aiEntry = Object.entries(agentMap).find(([, a]) => a.kind === 'ai');
    if (!aiEntry) return;
    const aiPlayerId = Number(aiEntry[0]);
    if (gameState.turnPlayerId !== aiPlayerId) return;
    const aiHasTower = gameState.players.find(p => p.id === aiPlayerId)!.towers.length > 0;
    if (aiHasTower) return;

    const timeoutId = setTimeout(() => {
      setGameState(prevState => {
        if (!prevState || prevState.phase !== GamePhase.TowerChoice || prevState.turnPlayerId !== aiPlayerId) return prevState;
        const aiChoice = getOptimalTowerColor(prevState, aiPlayerId);
        return gameLogic.chooseTower(prevState, aiPlayerId, aiChoice);
      });
    }, 800);
    return () => clearTimeout(timeoutId);
  }, [gameState, isMultiplayer, localPlayerId, playMode]);

  useEffect(() => {
    if (isMultiplayer) return;
    if (!gameState || gameState.phase !== GamePhase.StashChoice) return;
    const myId = localPlayerId;
    const aiId = myId === 1 ? 2 : 1;
    const myOptions = gameState.stashChoiceOptions[myId] || [];
    const aiOptions = gameState.stashChoiceOptions[aiId] || [];
    const aiPicksLeft = (gameState.stashChoicePicksRemaining?.[aiId] ?? 0);
    if (myOptions.length === 0 && aiOptions.length > 0 && aiPicksLeft > 0) {
      const timeoutId = setTimeout(() => {
        setGameState(prev => {
          if (!prev || prev.phase !== GamePhase.StashChoice) return prev;
          const opts = prev.stashChoiceOptions[aiId] || [];
          if (opts.length === 0) return prev;
          const choice = gameLogic.chooseBestStashCardId(prev, aiId, opts);
          return gameLogic.makeStashChoice(prev, aiId, choice.id, choice.fromBurned);
        });
      }, 600);
      return () => clearTimeout(timeoutId);
    }
  }, [gameState, isMultiplayer, localPlayerId]);

  useEffect(() => {
    if (isMultiplayer) return;
    if (!gameState || gameState.phase !== GamePhase.TowerChoice) return;
    const agentMap = agents.getAgentMap(gameState, playMode, localPlayerId);
    const aiEntry = Object.entries(agentMap).find(([, a]) => a.kind === 'ai');
    if (!aiEntry) return;
    const aiPlayerId = Number(aiEntry[0]);
    if (gameState.heroClassTurnId !== aiPlayerId) return;
    const aiPlayer = gameState.players.find(p => p.id === aiPlayerId)!;
    if (aiPlayer.hasChosenHeroAndClass) return;
    if (aiPlayer.towers.length === 0) return;
    const timeoutId = setTimeout(() => {
      setGameState(prevState => {
        if (!prevState || prevState.phase !== GamePhase.TowerChoice) return prevState;
        if (prevState.heroClassTurnId !== aiPlayerId) return prevState;
        const { hero, playerClass } = getBestHeroAndClass(prevState, aiPlayerId);
        return gameLogic.chooseHeroClass(prevState, aiPlayerId, hero, playerClass);
      });
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [gameState, isMultiplayer, localPlayerId, playMode]);

  // === UNIFIED AI COMBAT HANDLER ===
  // This single useEffect handles ALL AI actions during Combat/TowerPurchase phase.
  // Consolidating from two separate effects eliminates race conditions from overlapping timeouts.
  useEffect(() => {
    if (isMultiplayer) return; // Skip AI logic in multiplayer

    if (!gameState || !(gameState.phase === GamePhase.Combat || gameState.phase === GamePhase.TowerPurchase)) {
      return;
    }

    const localId = localPlayerId;
    const aiId = localId === 1 ? 2 : 1;
    const aiPlayer = gameState.players.find(p => p.id === aiId);
    if (!aiPlayer) return;

    // Determine if it's AI's turn using the centralized helper
    const isAITurn = agents.isAgentTurnToAct(gameState, aiId);
    if (!isAITurn) return;

    // Single timeout for AI action - avoids race conditions
    const timeoutId = setTimeout(async () => {
      const aiAgent = agents.createAIAgent();
      const action = await aiAgent.nextAction(gameState);

      if (!action) return;

      // Helper to execute actions with logging
      const executeAIAction = (gs: GameState, aiAction: gameLogic.AIAction) => {
        logAIAction(aiAction, gs, aiId);
        const prevState = JSON.parse(JSON.stringify(gs));

        const updateAndLog = (updateFn: (s: GameState) => GameState) => {
          setGameState(s => {
            if (!s) return s!;

            // === FRESH STATE VALIDATION ===
            // Re-check if AI can still act with current (fresh) state
            // This prevents stale closure issues
            const stillAITurn = agents.isAgentTurnToAct(s, aiId);
            if (!stillAITurn) {
              console.log('[AI] Action cancelled: no longer AI turn in fresh state');
              return s;
            }

            const newState = updateFn(s);
            setTimeout(() => logAIActionResult(aiAction, prevState, newState, aiId), 0);
            return newState;
          });
        };

        switch (aiAction.type) {
          case 'ATTEMPT_ACTIVATION':
            updateAndLog(s => gameLogic.attemptActivation(s, aiId, aiAction.cardId, aiAction.rotation, aiAction.targetId)); break;
          case 'ACCEPT_ACTIVATION':
            updateAndLog(s => gameLogic.acceptActivation(s, aiId)); break;
          case 'COUNTER_ACTIVATION':
            updateAndLog(s => gameLogic.counterActivation(s, aiId, aiAction.cardId, aiAction.rotation, aiAction.targetActivationId)); break;
          case 'PASS':
            updateAndLog(s => gameLogic.passTurn(s, aiId)); break;
          case 'MAGE_CAST_SPELL':
            updateAndLog(s => {
              const opponentId = aiId === 1 ? 2 : 1
              return gameLogic.mageCastSpellFromHand(
                s,
                aiId,
                aiAction.cardId,
                aiAction.rotation ?? 0,
                aiAction.targetId ?? opponentId,
                aiAction.targetActivationId
              )
            }); break;
          case 'ASTRID_BLAST':
            updateAndLog(s => gameLogic.astridHeroBlast(s, aiId, aiAction.targetId, aiAction.color)); break;
          case 'COMPLETE_BROKEN_SECRET':
            updateAndLog(s => gameLogic.completeBrokenSecret(s)); break;
          case 'DISCARD_STASH':
            updateAndLog(s => gameLogic.discardStashCardForEssence(s, aiId, aiAction.cardId)); break;
          case 'DISCARD_HAND_FOR_ESSENCE':
            updateAndLog(s => {
              if (s.phase === GamePhase.Preparation) {
                const s1 = gameLogic.toggleDiscardMode(s, aiId)
                const s2 = gameLogic.discardCardForEssence(s1, aiId, aiAction.cardId)
                return gameLogic.toggleDiscardMode(s2, aiId)
              }
              if (s.phase === GamePhase.Combat || s.phase === GamePhase.TowerPurchase) {
                const s1 = gameLogic.toggleDiscardMode(s, aiId)
                const s2 = gameLogic.discardCardForEssence(s1, aiId, aiAction.cardId)
                return gameLogic.toggleDiscardMode(s2, aiId)
              }
              return s
            }); break;
          case 'START_TOWER_PURCHASE':
            updateAndLog(s => gameLogic.startTowerPurchase(s)); break;
          case 'PURCHASE_TOWER':
            updateAndLog(s => gameLogic.purchaseTower(s, aiId, aiAction.color)); break;
          case 'OPEN_TOWER_CHANGE_MENU':
            updateAndLog(s => gameLogic.openTowerChangeMenu(s, aiId, aiAction.towerIndex)); break;
          case 'CHANGE_TOWER_COLOR':
            updateAndLog(s => gameLogic.changeTowerColor(s, aiAction.color)); break;
          case 'ROTATE_PREPARED': {
            // AI wants to rotate a prepared card by slot index
            updateAndLog(s => {
              const aiPlayer = s.players.find(p => p.id === aiId);
              if (!aiPlayer || aiAction.slotIndex >= aiPlayer.prepared.length) return s;
              const pc = aiPlayer.prepared[aiAction.slotIndex];
              if (!pc.card.typeB) return s; // Can't rotate a card without side B
              const newRotation = pc.rotation === 0 ? 180 : 0;
              const newPrepared = [...aiPlayer.prepared];
              newPrepared[aiAction.slotIndex] = { ...pc, rotation: newRotation };
              const newPlayers = s.players.map(p => p.id === aiId ? { ...p, prepared: newPrepared } : p) as [typeof s.players[0], typeof s.players[1]];
              return { ...s, players: newPlayers };
            }); break;
          }
        }
      };

      // Special handling for attack-type cards with targeting arrow animation
      if (action.type === 'ATTEMPT_ACTIVATION' && aiPlayer) {
        const preparedCard = aiPlayer.prepared.find(pc => pc.card.id === action.cardId);
        if (preparedCard) {
          const cardEffect = gameLogic.getCardEffect({ ...preparedCard, rotation: action.rotation });

          if ([CardType.Attack, CardType.Disease, CardType.NaturalGrip].includes(cardEffect.type) ||
            (preparedCard.card.essenceCost && preparedCard.card.type === CardType.Attack)) {
            const startRect = getPreparedCardRect(action.cardId);
            const endRect = getAnchorRect(UI_ANCHOR_IDS.localAvatar, playerAvatarRef.current);
            if (startRect && endRect) {
              setAiTargetingArrow({
                start: { x: startRect.left + startRect.width / 2, y: startRect.top + startRect.height / 2 },
                end: { x: endRect.left + endRect.width / 2, y: endRect.top + endRect.height / 2 },
                color: preparedCard.card.color
              });
              setTimeout(() => {
                setAiTargetingArrow(null);
                executeAIAction(gameState, action);
              }, 800);
              return; // prevent immediate execution
            }
          }
        }
      }

      // Default execution for non-attack or if arrow fails
      executeAIAction(gameState, action);
    }, currentActivation ? 2000 : 1500); // Slightly longer delay when responding to activation

    return () => clearTimeout(timeoutId);
  }, [gameState, isMultiplayer, currentActivation, localPlayerId, playMode]);

  const [showHonorFeedback, setShowHonorFeedback] = useState(false);
  useEffect(() => {
    if (!gameState) return;
    if (gameState.phase === GamePhase.GameOver && gameState.winner) {
      setShowHonorFeedback(true);
    } else {
      setShowHonorFeedback(false);
    }
  }, [gameState?.phase, gameState?.winner]);

  useEffect(() => {
    if (isMultiplayer) return;
    if (!gameState) return;
    const agentMap = agents.getAgentMap(gameState, playMode, localPlayerId);
    const aiEntry = Object.entries(agentMap).find(([, a]) => a.kind === 'ai');
    if (!aiEntry) return;
    const aiId = Number(aiEntry[0]);
    if (gameState.phase === GamePhase.AWAITING_CHROMABOW_CHOICE && gameState.chromabowInitiatorId === aiId) {
      const timeoutId = setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          if (prev.phase !== GamePhase.AWAITING_CHROMABOW_CHOICE) return prev;
          if (prev.chromabowInitiatorId !== aiId) return prev;
          const opponent = prev.players.find(p => p.id !== aiId)!;
          const pickOrder: CardColor[] = [CardColor.Red, CardColor.Black, CardColor.Yellow, CardColor.Green, CardColor.Purple, CardColor.Pink];
          const best = pickOrder.find(c => (prev.colorDecks[c]?.length || 0) > 0) ?? CardColor.Red;

          // Log the special action
          const colorNames: Record<CardColor, string> = {
            [CardColor.Red]: 'Red', [CardColor.Green]: 'Green', [CardColor.Black]: 'Black',
            [CardColor.Purple]: 'Purple', [CardColor.Yellow]: 'Yellow', [CardColor.Pink]: 'Pink'
          };
          logAISpecialAction('CHROMABOW_CHOICE', { 'Chosen Color': colorNames[best] }, aiId);

          return gameLogic.chooseChromabowColor(prev, aiId, best);
        });
      }, 900);
      return () => clearTimeout(timeoutId);
    }
    if (gameState.phase === GamePhase.AWAITING_HIBERNATION_CHOICE && gameState.hibernationInitiatorId === aiId) {
      const timeoutId = setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          if (prev.phase !== GamePhase.AWAITING_HIBERNATION_CHOICE) return prev;
          if (prev.hibernationInitiatorId !== aiId) return prev;
          const pickOrder: CardColor[] = [CardColor.Red, CardColor.Black, CardColor.Green, CardColor.Yellow, CardColor.Purple, CardColor.Pink];
          const best = pickOrder.find(c => (prev.colorDecks[c]?.length || 0) > 0) ?? CardColor.Red;

          // Log the special action
          const colorNames: Record<CardColor, string> = {
            [CardColor.Red]: 'Red', [CardColor.Green]: 'Green', [CardColor.Black]: 'Black',
            [CardColor.Purple]: 'Purple', [CardColor.Yellow]: 'Yellow', [CardColor.Pink]: 'Pink'
          };
          logAISpecialAction('HIBERNATION_CHOICE', { 'Chosen Color': colorNames[best] }, aiId);

          return gameLogic.chooseHibernationColor(prev, aiId, best);
        });
      }, 900);
      return () => clearTimeout(timeoutId);
    }
    if (gameState.phase === GamePhase.AWAITING_BURGLARY_CHOICE && gameState.burglaryInitiatorId === aiId) {
      const timeoutId = setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          if (prev.phase !== GamePhase.AWAITING_BURGLARY_CHOICE) return prev;
          if (prev.burglaryInitiatorId !== aiId) return prev;
          const defender = prev.players.find(p => p.id === prev.burglaryTargetId)!;
          const best = [...defender.hand]
            .sort((a, b) => ((b.essenceCost || 0) - (a.essenceCost || 0)) || ((b.value || 0) - (a.value || 0)))
          [0];
          if (!best) return prev;

          // Log the special action
          const cardName = best.faceSlug || best.type || 'Unknown Card';
          logAISpecialAction('BURGLARY_CHOICE', { 'Stolen Card': cardName }, aiId);

          return gameLogic.chooseBurglaryCard(prev, aiId, best.id);
        });
      }, 900);
      return () => clearTimeout(timeoutId);
    }
    // AI handles Purification target selection
    if (gameState.phase === GamePhase.AWAITING_PURIFICATION_TARGET && gameState.purificationInitiatorId === aiId) {
      const timeoutId = setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          if (prev.phase !== GamePhase.AWAITING_PURIFICATION_TARGET) return prev;
          if (prev.purificationInitiatorId !== aiId) return prev;
          const targetColor = getOptimalTowerColor(prev, aiId, true);

          // Log the special action
          const colorNames: Record<CardColor, string> = {
            [CardColor.Red]: 'Red', [CardColor.Green]: 'Green', [CardColor.Black]: 'Black',
            [CardColor.Purple]: 'Purple', [CardColor.Yellow]: 'Yellow', [CardColor.Pink]: 'Pink'
          };
          logAISpecialAction('PURIFICATION_TARGET', { 'Target Color': colorNames[targetColor] }, aiId);

          return gameLogic.choosePurificationTarget(prev, targetColor);
        });
      }, 900);
      return () => clearTimeout(timeoutId);
    }
    if (gameState.phase === GamePhase.LockedCardOverflow && gameState.overflowPlayerId === aiId) {
      const timeoutId = setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          if (prev.phase !== GamePhase.LockedCardOverflow) return prev;
          if (prev.overflowPlayerId !== aiId) return prev;

          const cardId = chooseLockedCardToReturnFromOverflow(prev, aiId);
          if (!cardId) return prev;

          const aiPlayer = prev.players.find(p => p.id === aiId);
          const card = aiPlayer?.prepared.find(pc => pc.card.id === cardId)?.card;
          const cardName = card?.faceSlug || card?.type || 'Unknown Card';
          logAISpecialAction('LOCKED_OVERFLOW_CHOICE', { 'Returned Card': cardName }, aiId);

          return gameLogic.returnLockedCardFromOverflow(prev, cardId);
        });
      }, 700);
      return () => clearTimeout(timeoutId);
    }
    if (gameState.towerChangeMenu && gameState.towerChangeMenu.playerId === aiId) {
      const timeoutId = setTimeout(() => {
        setGameState(prev => {
          if (!prev) return prev;
          if (!prev.towerChangeMenu || prev.towerChangeMenu.playerId !== aiId) return prev;
          const aiPlayer = prev.players.find(p => p.id === aiId)!;
          const opp = prev.players.find(p => p.id !== aiId)!;
          const needColor = opp.playerClass === 'MAGE' ? CardColor.Pink : opp.playerClass === 'OCCULTIST' ? CardColor.Black : opp.playerClass === 'WARRIOR' ? CardColor.Red : prev.towerChangeMenu.currentColor;
          const available = gameLogic.getAvailableColorsForTowerChange(prev);
          const pick = available.includes(needColor) ? needColor : available[0];
          if (!pick) return prev;

          // Log the special action
          const colorNames: Record<CardColor, string> = {
            [CardColor.Red]: 'Red', [CardColor.Green]: 'Green', [CardColor.Black]: 'Black',
            [CardColor.Purple]: 'Purple', [CardColor.Yellow]: 'Yellow', [CardColor.Pink]: 'Pink'
          };
          logAISpecialAction('TOWER_COLOR_CHANGE', { 'New Color': colorNames[pick] }, aiId);

          return gameLogic.changeTowerColor(prev, pick);
        });
      }, 800);
      return () => clearTimeout(timeoutId);
    }
  }, [gameState, isMultiplayer, localPlayerId, playMode]);

  useEffect(() => {
    if (!gameState) return;
    if (isMultiplayer) return;
    if (gameState.phase === GamePhase.Preparation) {
      setPlayerIsReady(false);
      setAiIsReady(false);
      const s1 = gameLogic.runAIDayActions(gameState);
      const s2 = gameLogic.runAIPreparationTurn(s1);

      // Log AI preparation actions
      const aiId = 2; // AI is always player 2 in single player
      const prevAI = gameState.players.find(p => p.id === aiId);
      const newAI = s2.newState.players.find(p => p.id === aiId);
      if (prevAI && newAI) {
        const preparedCards = s2.sequence
          .filter(s => s.action.type === 'PREPARE_CARD')
          .map(s => {
            // Use stateBefore from sequence to find the card before it was prepared
            const aiInStateBefore = s.stateBefore.players.find(p => p.id === aiId);
            const card = aiInStateBefore?.hand.find(c => c.id === (s.action as any).cardId);
            return {
              cardName: card?.faceSlug || card?.type || 'Unknown',
              rotation: (s.action as any).rotation || 0
            };
          });

        // Also log discards for essence
        s2.sequence
          .filter(s => s.action.type === 'DISCARD_HAND_FOR_ESSENCE' || s.action.type === 'DISCARD_STASH')
          .forEach(s => {
            logAIAction(s.action, s.stateBefore, aiId);
          });

        if (preparedCards.length > 0) {
          logAIPreparation(s2.newState, aiId, preparedCards);
        }
      }

      setGameState(s2.newState);
      setAiIsReady(true);
    }
    if (gameState.phase !== GamePhase.Preparation) {
      setAiIsReady(false);
    }
  }, [gameState?.phase, isMultiplayer]);

  const prevPhase = usePrevious(gameState?.phase);
  useEffect(() => {
    if (isMultiplayer) return;
    if (prevPhase === GamePhase.Combat && gameState && gameState.phase === GamePhase.RoundEnd) {
      const startNewRoundSequence = (stateBeforeNewRound: GameState) => {
        setTimeout(() => {
          const newState = gameLogic.startNewRound(stateBeforeNewRound);
          setGameState(newState);
          setPlayerIsReady(false);
          setIsDealing(false);
          setHandScroll(0);
        }, 1000);
      };

      startNewRoundSequence(gameState);
    }
  }, [gameState, prevPhase, isMultiplayer]);



  useEffect(() => {
    if (!gameState) { setHandGapIndex(null); return; }
    const isPrep = gameState.phase === GamePhase.Preparation;
    if (!isPrep) { setHandGapIndex(null); return; }
    if (suppressGapRef.current) { setHandGapIndex(null); return; }
    if (draggedCardInfo) {
      const id = draggedCardInfo.card.id;
      const playerHand = gameState.players.find(p => p.id === localPlayerId)!.hand;
      const grouped = groupHandForDisplay(playerHand);
      let idx = grouped.findIndex(g => g.cards.some(c => c.id === id));
      if (idx === -1) idx = Math.max(0, Math.min(grouped.length, handGapIndex ?? 0));
      setHandGapIndex(Math.max(0, Math.min(grouped.length, idx)));
    } else {
      setHandGapIndex(null);
    }
  }, [draggedCardInfo, gameState, localPlayerId, groupHandForDisplay]);

  useEffect(() => {
    if (!draggedCardInfo) setHandGapIndex(null);
    logSpace('drag.end', {});
  }, [draggedCardInfo]);

  useEffect(() => {
    if (!gameState || gameState.phase !== GamePhase.Preparation) return;
    if (!draggedCardInfo) setHandGapIndex(null);
    logSpace('phase.preparation.gap.clear', { dragging: !!draggedCardInfo });
  }, [gameState?.players, gameState?.phase, draggedCardInfo]);

  useEffect(() => {
    if (!gameState || gameState.phase !== GamePhase.Preparation) return;
    const playerLocal = gameState.players.find(p => p.id === localPlayerId)!;
    const display = groupHandForDisplay(playerLocal.hand);
    const keys = display.map(i => i.key);
    logSpace('hand.layout', { keys, handScroll, handGapIndex, daySpacing: 110 });
  }, [handLayoutTick, gameState?.phase, gameState?.players, localPlayerId, handScroll, handGapIndex]);



  useEffect(() => { logSpace('gapindex.change', { handGapIndex }); }, [handGapIndex]);






  const backgroundClass = (
    gameMode === 'PLAYING' && (gameState?.phase === GamePhase.Preparation || gameState?.phase === GamePhase.StashChoice || gameState?.phase === GamePhase.TowerChoice || gameState?.phase === GamePhase.LockedCardOverflow)
  )
    ? 'from-sky-500 via-orange-400 to-slate-900'
    : 'from-slate-900 to-indigo-900';




  // Show loading screen until assets are preloaded
  if (!assetsLoaded) {
    return <LoadingScreen progress={loadProgress} onComplete={() => setAssetsLoaded(true)} />;
  }

  if (gameMode !== 'PLAYING') {
    const renderLazyMenu = (node: React.ReactNode) => (
      <Suspense fallback={<LoadingScreen progress={100} />}>
        {node}
      </Suspense>
    );
    const shouldShowMenuUtilityBar = ['AUTH', 'MAIN_MENU', 'GAME_MODE_SELECT', 'MULTIPLAYER_MENU', 'CREATE_ROOM', 'JOIN_ROOM', 'PROFILE', 'OPTIONS'].includes(gameMode);

    const renderMenu = () => {
      switch (gameMode) {
        case 'AUTH':
          return <AuthScreen savedAuth={auth} onDone={handleAuthDone} language={language} />;
        case 'MAIN_MENU':
          return <MainMenu onNavigate={handleNavigate} onQuit={handleQuit} language={language} />;
        case 'GAME_MODE_SELECT':
          return <GameModeMenu onStartGame={() => { aiStartRef.current = Date.now(); handleStartGame(undefined, playerName); }} onNavigate={handleNavigate} onBack={() => handleNavigate('MAIN_MENU')} language={language} />;
        case 'MULTIPLAYER_MENU':
          return <MultiplayerMenu onNavigate={handleNavigate} onBack={() => handleNavigate('GAME_MODE_SELECT')} language={language} />;
        case 'CREATE_ROOM':
          return <CreateRoomScreen onCreateRoom={handleWsCreateRoom} onBack={() => handleNavigate('MULTIPLAYER_MENU')} defaultPlayerName={playerName} multiplayerState={multiplayerState} wsError={wsError} language={language} />;
        case 'JOIN_ROOM':
          return <JoinRoomScreen onJoinRoom={handleWsJoinRoom} onBack={() => handleNavigate('MULTIPLAYER_MENU')} defaultPlayerName={playerName} multiplayerState={multiplayerState} wsError={wsError} clearWsError={() => setWsError(null)} availableRooms={availableRooms} onRefreshRooms={requestRoomList} language={language} />;
        case 'ROOM_LOBBY':
          return multiplayerState.roomInfo && multiplayerState.localPlayerId ? (
            <RoomLobby
              room={multiplayerState.roomInfo}
              localPlayerId={multiplayerState.localPlayerId || ''}
              onLeave={handleWsLeaveRoom}
              onToggleReady={handleWsToggleReady}
              onStart={handleWsStartGame}
              onSelectSetup={(hero, playerClass) => {
                wsClient.send({ type: 'SELECT_LOBBY_SETUP', hero, playerClass });
              }}
              availableHeroes={availableHeroes}
              chatMessages={chatMessages}
              onSendChat={sendChatMessage}
              language={language}
              availableRooms={availableRooms}
              onSwitchRoom={handleSwitchRoom}
              onRefreshRooms={requestRoomList}
            />
          ) : null;
        case 'OPTIONS':
          return <OptionsScreen onBack={handleBack} language={language} />;
        case 'WORLD_MAP':
          return renderLazyMenu(
            <LazyWorldMap onBack={() => handleNavigate('MAIN_MENU')} gameState={gameState} language={language} />
          );
        case 'PROFILE':
          return renderLazyMenu(
            <LazyProfileScreen
              gameState={gameState}
              onBack={() => handleNavigate('MAIN_MENU')}
              availableHeroes={availableHeroes}
              language={language}
              onNavigate={handleNavigate}
              localPlayerId={multiplayerState.localPlayerNumber ?? undefined}
            />
          );
        case 'ADMIN_DASHBOARD':
          return renderLazyMenu(
            <LazyAdminPanel
              onBack={() => handleNavigate('MAIN_MENU')}
              language={language}
            />
          );
        case 'PREVIEWER':
          return renderLazyMenu(
            <LazyEffectPreviewer onExit={() => handleNavigate('MAIN_MENU')} />
          );
        default:
          return null;
      }
    };
    return (
      <div onClick={ensureMusicPlaying} className={`min-h-screen bg-transparent text-white flex flex-col p-4 font-sans transition-all duration-1000 ease-in-out`}>
        <AudioPlayer
          gameMode={gameMode}
          gameState={gameState}
          localPlayerId={localPlayerId}
          musicVolume={gameSettings.musicVolume}
          globalMute={gameSettings.globalMute}
        />
        <GameConnectionManager
          auth={auth}
          gameMode={gameMode}
          multiplayerState={multiplayerState}
          setGameMode={setGameMode}
          setMultiplayerState={setMultiplayerState}
          joinRoom={joinRoom}
          leaveRoom={leaveRoom}
          handleNavigate={handleNavigate}
          playerName={playerName}
          language={language}
        />
        {renderMenu()}
        <FullscreenButton />
        {gameMode !== 'PROFILE' && <GlobalMuteButton />}
        {showTriffleUnlock && (
          <TriffleUnlockOverlay
            language={language}
            onAccept={() => setShowTriffleUnlock(false)}
          />
        )}
        {shouldShowMenuUtilityBar && (
          <ShellSlot slot="top-utility-end">
            <div className="flex flex-wrap items-center justify-end gap-[9px] pointer-events-auto">
              {auth?.username && <NotificationBell username={auth.username} language={language} refreshTick={notifRefreshTick} />}
              <LanguageSwitcher currentLanguage={language} onLanguageChange={handleLanguageChange} />
            </div>
          </ShellSlot>
        )}
      </div>
    );
  }

  if (!gameState || !gameState.players) {
    return <div>Loading...</div>;
  }

  const player = gameState.players.find(p => p.id === localPlayerId)!;
  const opponent = gameState.players.find(p => p.id !== localPlayerId)!;
  const isPreparationPhase = gameState.phase === GamePhase.Preparation;

  const opponentCardCount = opponent.hand.length;
  const opponentHandSpacing = calcOpponentHandSpacing(opponentCardCount);

  const phaseMessage = getPhaseMessageUI(gameState, localPlayerId, playerIsReady && !isExtraDay, language);

  const playerHandDisplay = (() => {
    const baseHand = player.hand.filter(c => !(mageModalOpen && !!c.essenceCost && c.subType === 'Spell'))
    if (isPreparationPhase || gameState.discardMode?.[localPlayerId] === 'night') {
      return groupHandForDisplay(baseHand)
    }
    return baseHand.map(c => ({ key: c.id, topCard: c, cards: [c], count: 1 }))
  })();
  const cardCount = playerHandDisplay.length;
  const isHandScrollable = calcIsHandScrollable(cardCount, (handContainerRef.current?.offsetWidth || window.innerWidth));
  const nightSpacingBase = Math.max(20, 60 - player.hand.length * 2.5);
  const nightSpacingHand = Math.max(10, nightSpacingBase / 2);
  const nightSpacing = (gameState.phase === GamePhase.TowerPurchase) ? Math.max(8, nightSpacingHand * 0.7) : nightSpacingHand;
  const daySpacing = 110;






  const targetingArrowData = getTargetingArrowData(targeting, mousePos, aiTargetingArrow);
  const opponentAvatarRect = getAnchorRect(UI_ANCHOR_IDS.opponentAvatar, aiAvatarRef.current);
  const playerAvatarRect = getAnchorRect(UI_ANCHOR_IDS.localAvatar, playerAvatarRef.current);
  const deckZoneRect = getAnchorRect(UI_ANCHOR_IDS.deckZone, deckZoneRef.current);
  let isOverTarget = false;
  if (targetingArrowData) {
    const end = targetingArrowData.end;
    if (end) {
      if (opponentAvatarRect && end.x >= opponentAvatarRect.left && end.x <= opponentAvatarRect.right && end.y >= opponentAvatarRect.top && end.y <= opponentAvatarRect.bottom) {
        isOverTarget = true;
      } else if (playerAvatarRect && end.x >= playerAvatarRect.left && end.x <= playerAvatarRect.right && end.y >= playerAvatarRect.top && end.y <= playerAvatarRect.bottom) {
        isOverTarget = true;
      }
    }
  }

  // Use lastCardPlayerId as the source of truth for turn order
  // Player can respond when:
  // 1. There's at least one active (unresolved, unaccepted) activation
  // 2. The opponent played the last card (lastCardPlayerId !== localPlayerId)
  const hasActiveActivation = gameState.activations.some(act => !act.resolved && !act.accepted);
  const isMyTurnToRespond = hasActiveActivation &&
    gameState.lastCardPlayerId !== undefined &&
    gameState.lastCardPlayerId !== localPlayerId;
  const isOpponentStealActivation = (() => {
    if (!currentActivation) return false;
    if (currentActivation.initiatorId === localPlayerId) return false;
    const eff = gameLogic.getCardEffect(currentActivation.attackingCard);
    return eff.type === CardType.Steal;
  })();
  const canActivateDiscard = (
    (gameState.phase === GamePhase.Preparation && (!playerIsReady || isExtraDay)) ||
    ((gameState.phase === GamePhase.Combat || gameState.phase === GamePhase.TowerPurchase) && (
      ((gameState.turnPlayerId === localPlayerId && !currentActivation) ||
        isMyTurnToRespond) &&
      !isOpponentStealActivation
    ))
  );

  // --- Get all cards involved in any activation ---
  const allCardsInChains = gameState.activations.flatMap(act => [act.attackingCard, ...act.counterChain]);
  const allAttackingCardIds = new Set(gameState.activations.map(act => act.attackingCard.card.id));
  const allCounterChainCardIds = new Set(gameState.activations.flatMap(act => act.counterChain.map(pc => pc.card.id)));

  // --- Prepare card lists for rendering ---
  const getCardsForPrepArea = (p: Player): PreparedCard[] => {
    const cardMap = new Map<string, PreparedCard>();
    // Add prepared and resolved cards first
    [...p.prepared, ...p.resolved].forEach(pc => cardMap.set(pc.card.id, pc));
    // Add cards from active chains that belong to this player, ensuring they don't override existing ones
    allCardsInChains.forEach(pc => {
      if (pc.card.ownerId === p.id && !cardMap.has(pc.card.id)) {
        cardMap.set(pc.card.id, pc);
      }
    });
    return Array.from(cardMap.values());
  };

  const opponentCardsToRender = getCardsForPrepArea(opponent);
  const playerCardsToRender = getCardsForPrepArea(player);
  const canChangeTower = (gameState.phase === GamePhase.Combat || gameState.phase === GamePhase.TowerPurchase) && gameState.turnPlayerId === player.id && !currentActivation;

  return (
    <div onClick={ensureMusicPlaying} className={`min-h-screen max-h-screen overflow-hidden bg-transparent text-white flex flex-col p-4 font-sans transition-all duration-1000 ease-in-out`}>
      <TableBackground isDay={isPreparationPhase} />
      <AudioPlayer
        gameMode={gameMode}
        gameState={gameState}
        localPlayerId={localPlayerId}
        musicVolume={gameSettings.musicVolume}
        globalMute={gameSettings.globalMute}
      />
      <GameConnectionManager
        auth={auth}
        gameMode={gameMode}
        multiplayerState={multiplayerState}
        setGameMode={setGameMode}
        setMultiplayerState={setMultiplayerState}
        joinRoom={joinRoom}
        leaveRoom={leaveRoom}
        handleNavigate={handleNavigate}
        playerName={playerName}
        language={language}
      />
      {new URLSearchParams(window.location.search).get('test') === 'game' && (
        <Suspense fallback={null}>
          <LazyTestRunner />
        </Suspense>
      )}
      <ActionToast message={lastOpponentAction} />
      <ActionToast message={wsError ? t(wsError, language) : null} type="error" duration={3000} />
      <AnimationLayer animations={animations} onAnimationEnd={handleAnimationEnd} targets={{ playerAvatar: playerAvatarRect, aiAvatar: opponentAvatarRect, deckZone: deckZoneRect, localPlayerId }} language={language} />
      {/* Protector Defense: показываем ghost карты вместо стрелки */}
      {targeting && (targeting as any).source === 'protector-defense' && mousePos && (
        <div
          className="fixed pointer-events-none z-50 opacity-70 scale-75"
          style={{ left: mousePos.x - 50, top: mousePos.y - 70 }}
        >
          <Card card={(targeting as any).card.card} rotation={0} language={language} />
        </div>
      )}
      {/* Остальные targeting режимы: TargetingArrow */}
      {targetingArrowData && !(targeting && (targeting as any).source === 'protector-defense') && <TargetingArrow {...targetingArrowData} />}
      {aiTargetingArrow && <TargetingArrow {...aiTargetingArrow} />}
      <BattleLogHint
        visible={idleHintVisible}
        title={idleHintTitle}
        lines={idleHintLines}
        position="top-center"
        onClose={dismissIdleHint}
      />
      <BattleLogHint
        visible={hintVisible}
        title={hintTitle}
        lines={hintLines}
        icons={hintIcons}
        onClose={handleCloseReactionHint}
        maskingDelay={maskingDelay}
      />
      <ActivationChainLayer
        gameState={gameState}
        isMyTurnToRespond={isMyTurnToRespond}
        localPlayerId={localPlayerId}
        preparedCardRefs={preparedCardRefs}
        onDropOnActivationChain={(e: any, targetActivationId: any) => { markAction(); handleDropOnActivationChain(e, targetActivationId); handleCardDragEnd(); }}
        onDragOver={handleDragOver}
        onDragEnter={handleCounterDragEnter}
        onDragLeave={handleCounterDragLeave}
        isTouchDragging={!!draggedCardInfo}
        activationZonesRef={activationZonesRef}
        language={language}
      />
      {/* Activation Stack moved to BattleLog in sidebar */}

      {activationResolved && (
        <div
          className="absolute inset-0 flex items-center justify-center pointer-events-none z-50"
          onAnimationEnd={() => setActivationResolved(false)}
        >
          <div className="w-48 h-48 text-green-400" style={{ animation: 'checkmark-pop 1s ease-out forwards' }}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </div>
        </div>
      )}

      {gameState.phase === GamePhase.TowerChoice && gameState.turnPlayerId === localPlayerId && (player.towers.length === 0) && (
        <TowerChoiceModal
          onChoose={handleChooseTower}
          availableTowers={gameState.availableTowers}
          otherPlayerName={opponent.name}
          otherActionText={t('ui.isChoosingHeroClass', language)}
          language={language}
        />
      )}
      {gameState.phase === GamePhase.TowerChoice && (gameState.heroClassTurnId === localPlayerId) && (!player.hasChosenHeroAndClass) && (player.towers.length > 0) && (
        <HeroClassChoiceModal
          onChoose={handleChooseHeroClass}
          otherPlayerName={opponent.name}
          otherActionText={t('ui.isChoosingTower', language)}
          availableHeroes={isMultiplayer ? availableHeroes : undefined}
          language={language}
        />
      )}
      {gameState.phase === GamePhase.LorianClassChoice && (
        <HeroClassChoiceModal
          onChoose={(_, playerClass) => uiActions.handleChooseLorianClass(playerClass)}
          otherPlayerName={opponent.name}
          otherActionText={"is waiting for your choice"}
          availableHeroes={['LORIAN'] as HeroId[]}
          forceHero={'LORIAN'}
          language={language}
        />
      )}
      {gameState.phase === GamePhase.TowerChoice && (() => {
        const myTowerNeeded = player.towers.length === 0 && gameState.turnPlayerId === localPlayerId
        const myHeroNeeded = !player.hasChosenHeroAndClass && gameState.heroClassTurnId === localPlayerId
        const oppPending = (opponent.towers.length === 0) || !opponent.hasChosenHeroAndClass
        if (!myTowerNeeded && !myHeroNeeded && oppPending) {
          return (
            <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
              <div className="bg-black/60 px-6 py-3 rounded-lg border border-slate-600 text-slate-200 font-bold">
                Waiting for other player choice…
              </div>
            </div>
          )
        }
        return null
      })()}
      {gameState.phase === GamePhase.StashChoice && (gameState.stashChoiceOptions[localPlayerId]?.length || 0) > 0 && (
        // Stash Choice Modal
        <StashChoiceModal
          options={gameState.stashChoiceOptions[localPlayerId] || []}
          onChoose={handleStashChoice}
          onMouseEnterApp={handleCardMouseEnterApp}
          onMouseLeaveApp={handleCardMouseLeaveApp}
          viewerIsOccultist={player.playerClass === 'OCCULTIST'}
          burnedStashCards={gameState.burnedStashCards}
          heroId={player.hero}
          maxSelection={gameState.stashChoicePicksRemaining?.[localPlayerId] || 1}
          language={language}
        />
      )}
      {((gameState.phase === GamePhase.TowerPurchase && gameState.turnPlayerId === localPlayerId) || (gameState.phase === GamePhase.AWAITING_PURIFICATION_TARGET && gameState.purificationInitiatorId === localPlayerId)) && (
        <TowerPurchaseModal
          gameState={gameState}
          localPlayer={player}
          phase={gameState.phase}
          onPurchase={handlePurchaseTower}
          onCancel={handleCancelTowerPurchase}
          onPurifyTargetSelect={handleChoosePurificationTarget}
          language={language}
        />
      )}
      {protectorChoiceCardId && <ProtectorChoiceModal onChoose={handleProtectorModeChosen} language={language} />}
      {gameState.towerChangeMenu && gameState.towerChangeMenu.playerId === localPlayerId && (
        <TowerChangeModal
          gameState={gameState}
          onChoose={handleChangeTowerColor}
          onCancel={handleCloseTowerChangeMenu}
          language={language}
        />
      )}
      {gameState.phase === GamePhase.LockedCardOverflow && gameState.overflowPlayerId === localPlayerId && (
        <LockedCardOverflowModal gameState={gameState} onChoose={handleReturnLockedCardFromOverflow} language={language} />
      )}
      {gameState.phase === GamePhase.AWAITING_CHROMABOW_CHOICE && gameState.chromabowInitiatorId === localPlayerId && (
        <ChromabowChoiceModal
          onChoose={handleChooseChromabowColor}
          availableColors={Object.values(CardColor).filter(color => gameState.colorDecks[color]?.length > 0)}
          language={language}
        />
      )}
      {gameState.phase === GamePhase.AWAITING_HIBERNATION_CHOICE && gameState.hibernationInitiatorId === localPlayerId && (
        <HibernationChoiceModal
          onChoose={handleChooseHibernationColor}
          availableColors={Object.values(CardColor).filter(color => gameState.colorDecks[color]?.length > 0)}
          language={language}
        />
      )}
      {gameState.phase === GamePhase.AWAITING_BURGLARY_CHOICE && gameState.burglaryInitiatorId === localPlayerId && (() => {
        const target = gameState.players.find(p => p.id === gameState.burglaryTargetId)!
        const seen = new Set<string>()
        const cards = target.hand.filter(c => { if (seen.has(c.id)) return false; seen.add(c.id); return true; })
        const spacing = 110
        const count = cards.length
        return (
          <div className="fixed left-0 w-full h-40 flex items-center justify-center p-2 z-30" style={{ top: '50%', transform: 'translateY(-50%)' }}>
            {cards.map((c, index) => {
              const xOffset = (index - (count - 1) / 2) * spacing
              const onPick = () => uiActions.handleChooseBurglaryCard(c.id)
              return (
                <div key={c.id} className="transition-transform duration-500" style={{ position: 'absolute', left: '50%', transform: `translateX(calc(-50% + ${xOffset}px)) scale(1.1)` }}>
                  <div onClick={onPick} className="p-2 rounded-xl border-2 border-purple-500 bg-slate-900/80 shadow-xl cursor-pointer">
                    <Card card={c} rotation={0} onMouseEnterApp={handleCardMouseEnterApp} onMouseLeaveApp={handleCardMouseLeaveApp} language={language} />
                  </div>
                </div>
              )
            })}
          </div>
        )
      })()}
      {gameState.phase === GamePhase.BROKEN_SECRET_REVEAL && (
        <BrokenSecretRevealModal gameState={gameState} localPlayerId={localPlayerId} language={language} />
      )}

      {showTriffleUnlock && (
        <TriffleUnlockOverlay
          language={language}
          onAccept={() => setShowTriffleUnlock(false)}
        />
      )}

      {/* Life Charger Pending Phase - Small non-blocking notification banner */}
      {gameState.phase === GamePhase.LifeChargerPending && (() => {
        const dyingPlayerId = gameState.pendingLifeChargerPlayerId
        const isOpponentDying = dyingPlayerId !== localPlayerId
        const activation = gameState.activations[0]
        const lifeChargerCard = activation?.attackingCard?.card

        if (!activation || !lifeChargerCard) return null

        const dyingPlayerName = isOpponentDying ? opponent.name : player.name

        return (
          <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 pointer-events-none">
            <div className="bg-green-900/90 border border-green-500 rounded-lg px-4 py-2 shadow-lg text-center animate-pulse">
              <span className="text-green-300 font-bold">
                ⚡ {dyingPlayerName}'s Life Charger is activating!
              </span>
              {isOpponentDying && (
                <span className="text-slate-300 ml-2 text-sm">
                  (Counter with Psy-Block or Accept)
                </span>
              )}
            </div>
          </div>
        )
      })()}



      <GameOverFlow
        gameState={gameState}
        localPlayerId={localPlayerId}
        isMultiplayer={isMultiplayer}
        auth={auth}
        onNavigate={handleNavigate}
        onRestart={handleRestart}
        setPlayerIsReady={setPlayerIsReady}
        setGameState={setGameState}
        executeGameAction={executeGameAction}
        setAvailableHeroes={setAvailableHeroes}
        language={language}
      />

      <ActionToast message={toastMessage} type={toastType} />


      <div id="pause-button-container" className="fixed top-4 left-4 z-[100] flex flex-col items-start gap-2">
        <button
          onClick={toggleInGameMenu}
          className="w-12 h-12 bg-black/30 rounded-lg flex items-center justify-center text-white hover:bg-black/50 transition-colors"
          aria-label="Open game menu"
        >
          <MenuIcon className="w-6 h-6" />
        </button>
      </div>

      <div className="pointer-events-none fixed top-4 left-1/2 -translate-x-1/2 z-[100]">
        <h2 className="text-xl font-bold tracking-wider text-shadow-md bg-black/30 px-4 py-2 rounded-lg">{phaseMessage}</h2>
      </div>

      <div className="fixed top-4 right-4 z-20 flex flex-col items-end gap-4 pointer-events-none">
        {isMultiplayer && (
          <div className="z-50">
            <div className={`px-4 py-2 rounded-lg font-bold text-white ${multiplayerState.connectionStatus === 'CONNECTED' ? 'bg-green-600' :
              multiplayerState.connectionStatus === 'CONNECTING' ? 'bg-yellow-600' :
                'bg-red-600'
              }`}>
              {multiplayerState.connectionStatus === 'CONNECTED' ? '🟢 Connected' :
                multiplayerState.connectionStatus === 'CONNECTING' ? '🟡 Connecting...' :
                  '🔴 Disconnected'}
            </div>
          </div>
        )}
        <div className="group">
          <div className="group pointer-events-auto">
            <BattleLog
              gameState={gameState}
              localPlayerId={localPlayerId}
              onDropOnActivationChain={(e: any, targetId: any) => { markAction(); handleDropOnActivationChain(e, targetId); handleCardDragEnd(); }}
              onMouseEnterApp={handleCardMouseEnterApp}
              onMouseLeaveApp={handleCardMouseLeaveApp}
              language={language}
            />
          </div>
        </div>
        {localStorage.getItem('oko_is_admin') === 'true' && (
          <div className="group pointer-events-auto">
            <SeedManager seed={gameState.seed} onRestart={handleRestart} onAddToHand={(cardName, count, target) => {
              if (!gameState) return
              const targetPlayerId = target === 'me' ? localPlayerId : (localPlayerId === 1 ? 2 : 1)
              const action = { type: 'DEV_ADD_CARD' as const, playerId: localPlayerId, cardName, count, targetPlayerId }
              executeGameAction(
                action,
                (state) => applyGameAction(state, action) || state
              )
            }} />
          </div>
        )}
        <DeckInfoPanel
          ref={deckZoneRef}
          colorDecks={gameState.colorDecks}
          availableTowers={gameState.availableTowers}
          isDealing={isDealing}
          language={language}
        />
      </div>

      <GameViewport className="z-10">
        <div className="flex-1 flex flex-col items-center">
          <div className="w-full flex justify-between items-start px-4">
            <div className="flex items-start gap-4">
              <PlayerAvatar player={opponent} isOpponent={true} forwardedRef={aiAvatarRef} effectCardRefs={effectCardRefs} isAnimating={isCardAnimating} language={language} />
              <PlayerInfo player={opponent} language={language} isOpponent={true} canAcquireTower={false} />
            </div>
            <div className="h-28 flex-1 flex items-start justify-center p-2 relative mt-[-6rem]">
              {opponent.hand.map((card, index) => {
                const style = { position: 'absolute' as 'absolute', left: '50%', transform: `translateX(calc(-50% + ${(index - (opponentCardCount - 1) / 2) * opponentHandSpacing}px))` };
                return (<div key={`opponent-hand-${card.id}-${index}`} style={style}><Card card={card} isHidden={true} language={language} /></div>);
              })}
            </div>
          </div>

          <div className={`flex-grow w-full flex items-center justify-center gap-2 px-4 transition-transform duration-700 ease-in-out ${isPreparationPhase ? 'translate-y-[-25px]' : 'translate-y-0'}`}>
            <div className="h-32 flex-grow flex items-center justify-center gap-2 p-2 bg-black/20 rounded-lg min-h-[8.8rem] w-full relative">
              {gameState.revealedCard && (() => {
                const rc = gameState.revealedCard;
                const shouldShowDrawFace = rc.card.color === CardColor.Black && (rc.card.type === CardType.PsySearch || rc.card.typeB === CardType.PsySearch);
                const displayRotation = shouldShowDrawFace ? 0 : rc.rotation;
                return (
                  <div className="absolute z-30 pointer-events-none">
                    <Card card={rc.card} rotation={displayRotation} language={language} />
                  </div>
                );
              })()}
              {/* GLOBAL CHECK: Has player responded in ANY chain? */}
              {(() => {
                const playerHasResponded = gameState.activations.some(act => {
                  if (act.resolved || act.accepted) return false;
                  if (act.counterChain.length > 0) {
                    const lastInChain = act.counterChain[act.counterChain.length - 1];
                    return lastInChain.card.ownerId === localPlayerId;
                  }
                  return false;
                });
                return { playerHasResponded };
              })().playerHasResponded ? null : null}{/* Dummy to execute check */}
              {opponentCardsToRender.map(preparedCard => {
                const isActivatingForVisuals = allAttackingCardIds.has(preparedCard.card.id);
                const isActivatingOnTopChain = currentActivation?.attackingCard.card.id === preparedCard.card.id;
                const cardIsInChain = allCounterChainCardIds.has(preparedCard.card.id);

                const isResolved = opponent.resolved.some(pc => pc.card.id === preparedCard.card.id);
                const isLocked = !!preparedCard.lockedState;

                const isHidden = !isActivatingForVisuals &&
                  !isResolved &&
                  !isCardVisibleDueToDiscerningEye(gameState, preparedCard, opponent.id) &&
                  !isCardVisibleDueToPsyBlock(preparedCard);

                // GLOBAL CHECK: Opponent must have played the last card globally (it's my turn to respond)
                const opponentPlayedLastGlobally = gameState.lastCardPlayerId !== undefined &&
                  gameState.lastCardPlayerId !== localPlayerId;

                // PER-CHAIN CHECK: For THIS specific card, find the activation it belongs to
                const myActivationIndex = gameState.activations.findIndex(act =>
                  act.attackingCard.card.id === preparedCard.card.id && !act.resolved && !act.accepted
                );
                const myActivation = myActivationIndex >= 0 ? gameState.activations[myActivationIndex] : undefined;

                // PER-CHAIN: Check if the last card in THIS specific chain belongs to opponent
                let lastCardInChainIsOpponents = false;
                if (myActivation) {
                  const lastCardInChain = myActivation.counterChain.length > 0
                    ? myActivation.counterChain[myActivation.counterChain.length - 1]
                    : myActivation.attackingCard;
                  lastCardInChainIsOpponents = lastCardInChain.card.ownerId !== localPlayerId;
                }

                // Show Accept only if:
                // 1. Opponent played the last card globally (it's our turn to respond)
                // 2. This card is in an active activation
                // 3. The last card in THIS specific chain belongs to opponent
                const showAcceptButton = opponentPlayedLastGlobally && myActivation !== undefined && lastCardInChainIsOpponents;

                return (
                  <div key={`opponent-prepared-${preparedCard.card.id}`} ref={el => { preparedCardRefs.current[preparedCard.card.id] = el; }} className={`relative w-20 h-[7.7rem] flex-shrink-0 ${cardIsInChain ? 'opacity-0 pointer-events-none' : ''}`}>
                    {(() => {
                      const rotationForDisplay = (() => {
                        if (isActivatingOnTopChain && currentActivation) {
                          const eff = gameLogic.getCardEffect(currentActivation.attackingCard);
                          if (eff.type === CardType.PsySearch) return 0;
                          if (eff.type === CardType.PsyBlock) return 180;
                        }
                        return preparedCard.rotation;
                      })();
                      return (
                        <Card
                          card={preparedCard.card}
                          isHidden={isHidden}
                          isActivating={isActivatingForVisuals}
                          rotation={rotationForDisplay}
                          locked={isLocked}
                          viewerIsOccultist={gameState.players.find(p => p.id === localPlayerId)?.playerClass === 'OCCULTIST'}
                          language={language}
                        />
                      );
                    })()}
                    {showAcceptButton && (
                      <ActivationButton
                        onClick={() => {
                          markAction();
                          handleAcceptActivation();
                        }}
                        label="Accept"
                        language={language}
                      />
                    )}
                    {isActivatingOnTopChain && !currentActivation?.counterChain.length && isMyTurnToRespond && (
                      <div
                        className="absolute -inset-2 z-20"
                        onDrop={(e) => { markAction(); handleDropOnActivationChain(e, preparedCard.card.id); }}
                        onDragOver={handleDragOver}
                        onDragEnter={handleCounterDragEnter}
                        onDragLeave={handleCounterDragLeave}
                      />
                    )}
                    {showDefenseGhost && draggedCardInfo && isActivatingOnTopChain && (
                      <div className="absolute top-0 left-0 pointer-events-none opacity-50 z-30">
                        <Card card={draggedCardInfo.card} rotation={draggedCardInfo.rotation} language={language} />
                      </div>
                    )}
                  </div>
                );
              })}
              {(() => {
                const oppSlots = getAvailablePrepareSlots(gameState, opponent.id, GamePhase.Preparation); const count = Math.max(0, oppSlots - opponentCardsToRender.length); return Array(count).fill(0).map((_, i) => (
                  <div key={`placeholder-opponent-${i}`} className="w-20 h-[7.7rem] rounded-lg bg-slate-900/40 border-2 border-dashed border-slate-700"></div>
                ));
              })()}
            </div>
          </div>
        </div>

        <div className="h-16"></div>

        <div className="flex-1 flex flex-col items-center">
          <div className={`flex-grow w-full flex items-center justify-center gap-2 px-4 transition-transform duration-700 ease-in-out ${isPreparationPhase ? 'translate-y-[25px]' : 'translate-y-0'}`}>
            <div ref={prepZoneRef} className="h-32 flex-grow flex items-center justify-center gap-2 p-2 bg-black/20 rounded-lg min-h-[8.8rem] w-full" onDrop={handleDropOnPrepWrapped} onDragOver={handleDragOver}>
              {playerCardsToRender.map((preparedCard, index) => {
                // Determine lock status
                const isLocked = !!preparedCard.lockedState;
                const isRotationLocked = isLocked;
                let isTemporarilyUnusable = false;
                if (isLocked) {
                  const roundsSinceLocked = gameState.round - preparedCard.lockedState!.appliedRound;
                  if (roundsSinceLocked === 0) {
                    isTemporarilyUnusable = true;
                  }
                }

                // Determine the card's current, single effect based on its state
                const currentEffect = gameLogic.getCardEffect(preparedCard);

                // Determine targetable status based on the current effect
                const isTargetableCard = gameLogic.isTargetableEffect(currentEffect.type) || (preparedCard.card.essenceCost && gameLogic.isTargetableEffect(preparedCard.card.type));

                // Determine if the card has a shield/reaction effect available
                let hasShieldEffect;
                if (isLocked) {
                  // For locked cards, only the current effect matters
                  hasShieldEffect = gameLogic.isShieldEffect(currentEffect.type) || preparedCard.card.extendedShield;
                  if (isMyTurnToRespond && gameState.turnPlayerId === player.id && !player.hasPassedActiveTurn) {
                    if (currentEffect.type === CardType.DoubleCross) {
                      hasShieldEffect = true;
                    }
                  }
                } else {
                  // For unlocked cards, check both sides
                  const effectA = gameLogic.getCardEffect({ ...preparedCard, rotation: 0 });
                  const effectB = preparedCard.card.typeB ? gameLogic.getCardEffect({ ...preparedCard, rotation: 180 }) : null;
                  hasShieldEffect = gameLogic.isShieldEffect(effectA.type) || (effectB && gameLogic.isShieldEffect(effectB.type)) || preparedCard.card.extendedShield;
                  if (isMyTurnToRespond && gameState.turnPlayerId === player.id && !player.hasPassedActiveTurn) {
                    if (effectA.type === CardType.DoubleCross || (effectB && effectB.type === CardType.DoubleCross)) {
                      hasShieldEffect = true;
                    }
                  }
                }

                const isActivatingForVisuals = allAttackingCardIds.has(preparedCard.card.id);
                const isActivatingOnTopChain = currentActivation?.attackingCard.card.id === preparedCard.card.id;
                const cardIsInChain = allCounterChainCardIds.has(preparedCard.card.id);

                // GLOBAL CHECK: Opponent must have played the last card globally
                const opponentPlayedLastGlobally = gameState.lastCardPlayerId !== undefined &&
                  gameState.lastCardPlayerId !== localPlayerId;

                // PER-CHAIN CHECK: For THIS specific card, check if it's in an activation
                const myActivation = gameState.activations.find(act => {
                  // Check if this card is the attacking card OR in the counter chain
                  const isAttackingCard = act.attackingCard.card.id === preparedCard.card.id;
                  const isInCounterChain = act.counterChain.some(c => c.card.id === preparedCard.card.id);
                  return (isAttackingCard || isInCounterChain) && !act.resolved && !act.accepted;
                });

                // PER-CHAIN: Check if the last card in THIS specific chain belongs to opponent
                let lastCardInChainIsOpponents = false;
                if (myActivation) {
                  const lastCardInChain = myActivation.counterChain.length > 0
                    ? myActivation.counterChain[myActivation.counterChain.length - 1]
                    : myActivation.attackingCard;
                  lastCardInChainIsOpponents = lastCardInChain.card.ownerId !== localPlayerId;
                }

                // Show Accept only if:
                // 1. Opponent played the last card globally (it's our turn to respond)
                // 2. This card is in an active activation
                // 3. The last card in THIS specific chain belongs to opponent
                const showConfirmButton = opponentPlayedLastGlobally && myActivation !== undefined && lastCardInChainIsOpponents;
                const canActivate = gameState.phase === GamePhase.Combat && gameState.turnPlayerId === localPlayerId && !currentActivation;

                const canPlayAfterPassing = !player.hasPassedActiveTurn || gameLogic.isShieldEffect(currentEffect.type) || preparedCard.card.playableAfterPass;
                const effectiveCost = preparedCard.card.essenceCost ? gameLogic.getEffectiveEssenceCostForPlayer(preparedCard.card, player) : 0;
                const isPlayable = canPlayAfterPassing && !gameState.discardMode?.[localPlayerId] && (isPreparationPhase || !(preparedCard.card.essenceCost && player.essence < effectiveCost)) && !isTemporarilyUnusable;
                const isInteractive = ((isPreparationPhase || canActivate) || (isMyTurnToRespond && hasShieldEffect)) && !isTemporarilyUnusable;

                return (
                  <div id={preparedCard.card.id.toString()} key={`${preparedCard.card.id}-me-${index}`} ref={el => { preparedCardRefs.current[preparedCard.card.id] = el; }} className={`relative w-20 h-[7.7rem] flex-shrink-0 ${isCardAnimating(preparedCard.card.id) ? 'opacity-0' : ''} ${cardIsInChain ? 'opacity-0 pointer-events-none' : ''}`}>
                    <TouchCardWrapper
                      card={preparedCard.card}
                      isInteractive={isInteractive}
                      isClickable={(isPreparationPhase || (!isTargetableCard && canActivate)) && !isTemporarilyUnusable}
                      isPlayable={isPlayable}
                      isTargetable={isTargetableCard && canActivate && !isTemporarilyUnusable}
                      source={isMyTurnToRespond && hasShieldEffect ? 'prepared-reaction' : 'prepared'}
                      rotation={preparedCard.rotation}
                      rotationLocked={isRotationLocked}
                      viewerIsOccultist={player.playerClass === 'OCCULTIST'}
                      onRotationChange={(id, rot) => { markAction(); handlePreparedCardRotation(id, rot); }}
                      onClick={!isTargetableCard ? () => { markAction(); uiActions.handlePreparedCardClick(preparedCard.card.id, preparedCard.rotation); } : undefined}
                      onMouseDown={isTargetableCard ? (e) => { markAction(); handleTargetableMouseDown(e, preparedCard); } : undefined}
                      onDoubleClick={(id, e) => { if (isPreparationPhase && !playerIsReady) { markAction(); handlePreparedDoubleClick(e, id); } else { handleNightDiscard(id); } }}
                      onDragStartApp={(c, r, s) => { markAction(); handleCardDragStart(c, r, s); }}
                      onDragEndApp={() => { markAction(); handleCardDragEnd(); }}
                      onTouchDrop={handleTouchDrop}
                      // Touch targeting callbacks for targetable cards (Attack, Disease, etc.)
                      onTouchTargetStart={(isTargetableCard && canActivate && !isTemporarilyUnusable) ? (startRect: DOMRect) => {
                        console.log('[App] Touch targeting started for:', preparedCard.card.id);
                        setTargeting({ card: preparedCard, startRect });
                        setMousePos({ x: startRect.left + startRect.width / 2, y: startRect.top + startRect.height / 2 });
                      } : undefined}
                      onTouchTargetMove={(isTargetableCard && canActivate && !isTemporarilyUnusable) ? (x: number, y: number) => {
                        setMousePos({ x, y });
                      } : undefined}
                      onTouchTargetEnd={(isTargetableCard && canActivate && !isTemporarilyUnusable) ? (dropX: number, dropY: number) => {
                        console.log('[App] Touch targeting ended at:', { dropX, dropY });
                        // Use the rotation from targeting state if available, otherwise fallback to card's rotation
                        const finalRotation = (targeting as any)?.card?.rotation ?? preparedCard.rotation;

                        // Check avatar targets
                        const aiRect = getAnchorRect(UI_ANCHOR_IDS.opponentAvatar, aiAvatarRef.current);
                        const playerRect = getAnchorRect(UI_ANCHOR_IDS.localAvatar, playerAvatarRef.current);
                        const opponentPlayer = gameState?.players.find(p => p.id !== localPlayerId);

                        if (aiRect && dropX >= aiRect.left && dropX <= aiRect.right &&
                          dropY >= aiRect.top && dropY <= aiRect.bottom && opponentPlayer) {
                          console.log('[App] Touch target hit opponent avatar');
                          markAction();
                          handleActivateCard(preparedCard.card.id, finalRotation, opponentPlayer.id);
                        } else if (playerRect && dropX >= playerRect.left && dropX <= playerRect.right &&
                          dropY >= playerRect.top && dropY <= playerRect.bottom) {
                          console.log('[App] Touch target hit player avatar');
                          markAction();
                          handleActivateCard(preparedCard.card.id, finalRotation, localPlayerId);
                        }
                        setTargeting(null);
                        setMousePos(null);
                      } : undefined}
                      onTouchTargetCancel={(isTargetableCard && canActivate && !isTemporarilyUnusable) ? () => {
                        console.log('[App] Touch targeting cancelled');
                        setTargeting(null);
                        setMousePos(null);
                      } : undefined}
                      onMouseEnterApp={handleCardMouseEnterApp}
                      onMouseLeaveApp={handleCardMouseLeaveApp}
                      isGlowingForNightDiscard={(() => {
                        const isEssenceActive = !!gameState.discardMode?.[localPlayerId];
                        const isMyControl = isPreparationPhase || gameState.turnPlayerId === localPlayerId || isMyTurnToRespond;
                        if (!isEssenceActive || !isMyControl) return false;
                        if (preparedCard.card.essenceCost) return true;
                        if (preparedCard.lockedState) {
                          const roundsSinceLocked = gameState.round - preparedCard.lockedState.appliedRound;
                          if (roundsSinceLocked === 0) return false;
                        }
                        return true;
                      })()}
                      isGlowingForReaction={isMyTurnToRespond && hasShieldEffect}
                      isActivating={isActivatingForVisuals}
                      locked={isTemporarilyUnusable}
                      onFlourishTrigger={handleFlourishTrigger}
                      language={language}
                    />
                    {/* Wave Signal for action hints */}
                    {(() => {
                      const hint = getHint(preparedCard.card.id);
                      if (!hint || !hintsEnabled) return null;
                      return (
                        <CardWaveSignal
                          isAIBest={hint.priority === 'AI_BEST'}
                          cardColor={preparedCard.card.color}
                          topValid={hint.validSides?.top}
                          bottomValid={hint.validSides?.bottom}
                          rotation={preparedCard.rotation}
                        />
                      );
                    })()}
                    {showConfirmButton && (
                      <ActivationButton
                        onClick={() => {
                          markAction();
                          handleAcceptActivation();
                        }}
                        label="Accept"
                        language={language}
                      />
                    )}
                  </div>
                )
              })}
              {(() => {
                const meSlots = getAvailablePrepareSlots(gameState, player.id, GamePhase.Preparation);
                const count = Math.max(0, meSlots - playerCardsToRender.length);
                const hint = hintsEnabled && getHint('prepared-slot-empty');
                return Array(count).fill(0).map((_, i) => (
                  <div key={`placeholder-player-${i}`} className="relative w-20 h-[7.7rem] rounded-lg bg-slate-900/40 border-2 border-dashed border-slate-700">
                    {hint && (
                      <ElementWaveSignal
                        isAIBest={hint.priority === 'AI_BEST'}
                        shape="RECTANGLE"
                      />
                    )}
                  </div>
                ));
              })()}
            </div>
          </div>
          <div className="w-full flex justify-between items-end px-4">
            <div className="flex items-end gap-0">
              <PlayerAvatar player={player} forwardedRef={playerAvatarRef} effectCardRefs={effectCardRefs} isAnimating={isCardAnimating} language={language} />
              <HeroAbilityHUD
                player={player}
                gameState={gameState}
                setTargeting={setTargeting}
                setMousePos={setMousePos}
                playerAvatarRef={playerAvatarRef}
                language={language}
              />
              <div id="spells-essence-container" style={{ transform: 'translateX(-30px)' }} className="relative">
                {gameState.phase === GamePhase.Combat && player.playerClass === 'MAGE' && (
                  <div id="mage-spells-button" className="absolute left-1/2 w-14 h-14" style={{ top: '-39px', transform: 'translateX(calc(-50% + 70px))', touchAction: 'none', zIndex: 40 }}>
                    {getHint('mage-spells-button') && hintsEnabled && (
                      <ElementWaveSignal
                        isAIBest={getHint('mage-spells-button')!.priority === 'AI_BEST'}
                        elementType="BUTTON"
                        shape="CIRCLE"
                      />
                    )}
                    <button onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const act = gameState.activations.length > 0 ? gameState.activations[gameState.activations.length - 1] : null;
                      const responderId = act ? (act.initiatorId === 1 ? 2 : 1) : null;
                      const canOpen = (gameState.turnPlayerId === localPlayerId) || (act && act.state === 'PENDING_RESPONSE' && responderId === localPlayerId);
                      if (!canOpen) return;
                      setMageModalOpen(prev => !prev);
                    }}
                      className="w-full h-full rounded-full bg-slate-900/95 border-2 border-pink-500 shadow-[0_0_15px_rgba(236,72,153,0.6)] flex items-center justify-center hover:scale-105 active:scale-95 transition-all duration-200"
                    >
                      <span className="text-[10px] font-bold text-pink-300 uppercase tracking-wider">{t('ui.spells', language)}</span>
                    </button>
                  </div>
                )}
                <EssenceManager
                  essence={player.essence}
                  onClick={handleToggleDiscardMode}
                  isActive={!!gameState.discardMode?.[localPlayerId]}
                  canActivate={canActivateDiscard}
                  language={language}
                  hint={getHint('essence-mode-button') && hintsEnabled ? { isAIBest: getHint('essence-mode-button')!.priority === 'AI_BEST' } : undefined}
                />
              </div>
              <div className="flex flex-col items-center">
                <PlayerInfo
                  player={player}
                  language={language}
                  onAcquireTowerClick={handleStartTowerPurchase}
                  onOwnedTowerClick={canChangeTower ? handleOpenTowerChangeMenu : undefined}
                  canAcquireTower={!player.capturedTowerThisTurn && gameState.phase === GamePhase.Combat}
                  hint={getHint('capture-tower-button') && hintsEnabled ? { isAIBest: getHint('capture-tower-button')!.priority === 'AI_BEST' } : undefined}
                />
              </div>
            </div>
            <div key={`${handLayoutTick}-${playerHandDisplay.map(i => i.key).join('|')}`} ref={handContainerRef} className={`transition-all duration-700 ease-in-out ${(isPreparationPhase || !!gameState.discardMode?.[localPlayerId]) ? 'absolute top-1/2 left-0 w-full h-40 flex items-center justify-center p-2 z-10 -translate-y-[calc(50%-25px)]' : 'h-40 flex-1 flex items-end justify-center p-2 relative'} ${gameState.discardMode?.[localPlayerId] ? 'essence-mode-hand' : ''}`} onDrop={handleHandContainerDrop} onDragOver={(e) => {
              e.preventDefault();
              if (!isPreparationPhase) return;
              if (!draggedCardInfo) return;
              if (suppressGapRef.current) return;
              const rect = getAnchorRect(UI_ANCHOR_IDS.handRail, handContainerRef.current);
              if (!rect) return;
              const centerX = rect.left + rect.width / 2;
              const x = (e as any).clientX - centerX - handScroll;
              const idxFloat = x / daySpacing + ((cardCount - 1) / 2);
              const idx = Math.max(0, Math.min(cardCount, Math.round(idxFloat)));
              setHandGapIndex(idx);
              logSpace('hand.dragover', { clientX: (e as any).clientX, centerX, handScroll, idxFloat, idx, cardCount });
            }}>
              <HandScroller enabled={(isPreparationPhase || !!gameState.discardMode?.[localPlayerId]) && isHandScrollable && !isTouchDevice()} onScrollUpdate={updateScrollSpeed} />
              {playerHandDisplay.map((item, index) => {
                const { topCard, count, key } = item;
                const isEssenceMode = !!gameState.discardMode?.[localPlayerId];
                const style = (isPreparationPhase || isEssenceMode)
                  ? { position: 'absolute' as 'absolute', left: '50%', transform: `translateX(calc(-50% + ${(() => { const baseIndexTerm = (index - (cardCount - 1) / 2) * daySpacing; const gapActive = !!draggedCardInfo && handGapIndex !== null; const gapOffset = (gapActive && index >= handGapIndex!) ? daySpacing : 0; const base = handScroll + baseIndexTerm + gapOffset; return base; })()}px))` }
                  : { position: 'absolute' as 'absolute', left: '50%', transform: `translateX(calc(-50% + ${(index - (cardCount - 1) / 2) * nightSpacing}px)) rotate(${(index - (cardCount - 1) / 2) * 2}deg)` };
                const rotation = handCardRotations[topCard.id] || 0;
                return (<div id={topCard.id.toString()} key={`hand-${key}-${index}`} ref={el => { handGroupRefs.current[key] = el; }} className={`transition-transform duration-300 ${isCardAnimating(topCard.id) ? 'opacity-0' : ''}`} style={style}>
                  <TouchCardWrapper
                    card={topCard}
                    count={count}
                    groupKey={key}
                    isInteractive={isPreparationPhase || gameState.discardMode?.[localPlayerId] === 'night'}
                    isPlayable={true}
                    source="hand"
                    rotation={rotation}
                    viewerIsOccultist={player.playerClass === 'OCCULTIST'}
                    onRotationChange={handleHandCardRotation}
                    onDropOnGroup={handleReorderHand}
                    onDragOverCard={handleDragOver}
                    onDoubleClick={(id, _e) => { markAction(); handleDayDiscard(id); }}
                    onDragStartApp={(c, r, s) => { markAction(); handleCardDragStart(c, r, s); }}
                    onDragEndApp={() => { markAction(); handleDragEndAppWrapped(); }}
                    onTouchDrop={handleTouchDrop}
                    onMouseEnterApp={handleCardMouseEnterApp}
                    onMouseLeaveApp={handleCardMouseLeaveApp}
                    isGlowingForDayDiscard={(gameState.discardMode?.[localPlayerId] === 'day' || gameState.discardMode?.[localPlayerId] === 'night') && !topCard.essenceCost}
                    isGlowingForNightDiscard={(() => {
                      const isEssenceActive = !!gameState.discardMode?.[localPlayerId];
                      const isMyControl = isPreparationPhase || gameState.turnPlayerId === localPlayerId || isMyTurnToRespond;
                      if (!isEssenceActive || !isMyControl) return false;
                      if (topCard.essenceCost) return false;
                      return true;
                    })()}
                    onFlourishTrigger={handleFlourishTrigger}
                    language={language}
                  />
                </div>);
              })}
            </div>
          </div>
        </div>
      </GameViewport>

      {/* Pass/End Day Buttons */}
      <HUD
        phase={gameState.phase}
        isMyTurn={gameState.turnPlayerId === localPlayerId}
        onEndDay={handleEndDay}
        onPass={handlePassTurn}
        endDayDisabled={playerIsReady && !isExtraDay}
        passDisabled={(gameState.phase !== GamePhase.Combat && gameState.phase !== GamePhase.TowerPurchase) || mugController.passButtonLocked}
        language={language}
        endDayHint={hintsEnabled && getHint('end-day-button') ? { isAIBest: getHint('end-day-button')!.priority === 'AI_BEST' } : undefined}
        passHint={hintsEnabled && getHint('pass-button') ? { isAIBest: getHint('pass-button')!.priority === 'AI_BEST' } : undefined}
      />

      {/* Static Mug Idle Frame - rendered behind animations */}
      <StaticMug ref={staticMugRef} cheersPlaying={cheersPlaying} mugRequest={mugController.mugRequest} />

      {/* Cheers Animation Overlay */}
      <CheersAnimation
        phase={gameState.phase}
        playKey={mugController.cheersPlayKey}
        onPlayingChange={setCheersPlaying}
        onFrameNearEnd={() => staticMugRef.current?.handleCheersNearEnd()}
      />

      {/* TakePutMug Animation - plays after Cheers ends during night */}
      <TakePutMugAnimation
        request={mugController.mugRequest}
        onDone={mugController.onMugDone}
        onHalfComplete={mugController.onHalfComplete}
        onFrameNearEnd={(half, direction) => staticMugRef.current?.handleMugFrameNearEnd(half, direction)}
      />

      <div className={`fixed bottom-6 right-[-5.5rem] z-10 transition-transform duration-500 ease-in-out flex flex-col-reverse items-center gap-4 group hover:translate-x-[-6rem] ${isDealing ? 'translate-x-[-6rem]' : 'translate-x-0'}`}>
        <StashDeckDisplay count={gameState.stashDeck.length} />
        <div className={`transition-opacity duration-300 ${isDealing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}><DealingDeckDisplay count={gameState.dealingDeck.length} /></div>
      </div>

      {
        showInGameMenu && (
          <InGameMenu
            onResume={toggleInGameMenu}
            onNavigate={handleNavigate}
            onReturnToMainMenu={confirm.handleReturnToMainMenu}
            language={language}
          />
        )
      }

      {
        showConfirmDialog.visible && (
          <ConfirmDialog
            message={showConfirmDialog.message}
            onConfirm={confirm.handleConfirmAction}
            onCancel={confirm.handleHideConfirm}
            language={language}
          />
        )
      }



      <MageSpellModal
        isOpen={mageModalOpen}
        onClose={() => setMageModalOpen(false)}
        localPlayerId={localPlayerId}
        players={gameState.players}
        activations={gameState.activations}
        onCastFromHand={(cardId) => { uiActions.handleMageCastFromHand(cardId); setMageModalOpen(false); }}
        setTargeting={setTargeting}
        setMousePos={setMousePos}
        preparedCardRefs={preparedCardRefs}
        handleCardMouseEnter={handleCardMouseEnterApp}
        handleCardMouseLeave={handleCardMouseLeaveApp}
        language={language}
      />


      {/* Flourish Animation Layer */}
      {flourishAnimations.map(anim => (
        <FlourishCard
          key={anim.id}
          animation={anim}
          onEnd={handleFlourishEnd}
          language={language}
        />
      ))}

      <InstructorGuide
        gameState={gameState}
        localPlayerId={localPlayerId}
        language={language}
        isPaused={showInGameMenu || !!draggedCardInfo || !!targeting}
      />

      {['AUTH', 'MAIN_MENU', 'GAME_MODE_SELECT', 'MULTIPLAYER_MENU', 'CREATE_ROOM', 'JOIN_ROOM', 'PROFILE', 'OPTIONS'].includes(gameMode) && (
        <div className="fixed top-[31px] right-[31px] z-[9999] flex gap-[9px] items-center pointer-events-auto">
          {auth?.username && <NotificationBell username={auth.username} language={language} refreshTick={notifRefreshTick} />}
          <LanguageSwitcher currentLanguage={language} onLanguageChange={handleLanguageChange} />
        </div>
      )}
    </div >
  );
};

export default App;

